import {css, html, LitElement, nothing} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import Color from 'colorjs.io';

import {ActivityQueueItem} from './activity-queue-item';
import './activity-queue-item';
import './log';
import './activity-log-entry';
import './resource';
import './resources';
import './stat';
import './activity';
import './activities';
import './tutorial';
import './settings';
import './modal';
import './perceptions-minigame';
import './spell';
import './spells';
import './smooth-value';
import './filter-view';
import './filter-entry';
import './unlockables';
import './unlockables-entry';
import './plot';

import {Activity, ActivityOption} from './activity';
import {IQueueable} from './interfaces/queueable';
import global_css from './global-css';
import {Log, LogEntry} from './log';
import {UnlockableLitElement} from './unlockable';
import {Unlockables} from './unlockables';
import {Resource} from './resource';
import Decimal, {DecimalSource} from './break_infinity.js';
import {Stat} from './stat';
import {Spell} from './spell';
import {IValuable} from './interfaces/valuable';
import settings, {Settings} from './settings';
import {
  activitiesByTag,
  fancy,
  formatEng,
  formatTime,
  logisticChance,
  sumActivitiesByTag,
} from './utils';
import {NamedValue} from './types/named-value';
import {ActivityLogEntry} from './activity-log-entry';
import {Cost, DynamicCost} from './interfaces/dynamic-value';

let saveGame = JSON.parse(localStorage.getItem('saveGame') || 'null');
const saveVersion = 6;

const tickLength = 1000;
let lastTime = Date.now();
let lastSpellCooldownTime = Date.now();
let lastSaved = Date.now();

let oldStats: NamedValue[] = [];
let oldResources: NamedValue[] = [];

// I know.
export const ri = document.querySelector('runner-idle') as RunnerIdle;
window.ri = ri; // Just for live working on it.
window.Decimal = Decimal;

let firstPaused = false;
function StartGameLoop() {
  requestAnimationFrame(() => StartGameLoop());
  const now = Date.now();
  // If we're paused or lots of time has passed (background tab, really slow machine?),
  // bank the time and requestUpdate just in case.
  if (ri.gameSpeed == 0 || now - lastTime > 2000) {
    ri.resources.time.value = ri.resources.time.value.plus(
      (now - lastTime) / 1000
    );
    if (firstPaused) {
      // updateSpellCooldowns(lastTime - lastSpellCooldownTime);
      firstPaused = false;
    }
    lastSpellCooldownTime = now;
    lastTime = now;
    ri.requestUpdate();
    updateUnlocks();
    save();
  }
  // If we're not paused, update the game.
  else {
    firstPaused = true;
    // If we're using saved time and we have some, use it.
    if (
      ri.useSavedTime &&
      ri.resources.time.value.gte(tickLength / 1000) &&
      new Decimal(1000)
        .div(ri.resources.time.value.div(10).max(10))
        .lt(now - lastTime)
    ) {
      // updateSpellCooldowns(tickLength);
      lastSpellCooldownTime = now;
      lastTime = now;
      gameLoop();
      ri.resources.time.value = ri.resources.time.value.minus(
        tickLength / 1000
      );
    }
    // Otherwise just tick if possible
    else {
      if (lastSpellCooldownTime - lastTime > 900)
        lastSpellCooldownTime = lastTime - 1000;
      // updateSpellCooldowns(now - lastSpellCooldownTime);
      lastSpellCooldownTime = now;
      if (now - lastTime > tickLength) {
        gameLoop();
        lastTime += tickLength;
      }
    }
  }
}

const updateSpellCooldowns = (dt: number) => {
  // Realtime spells are updated in a separate loop (see spells.ts)
  for (const spell of Object.values(ri.spells).filter(
    (spell) => !spell.tags.has('realtime')
  )) {
    if (spell.currentCooldown.gt(0)) {
      spell.currentCooldown = spell.currentCooldown.minus(dt / 1000);
    }
  }
};

/**
 * This is the order of when things happen in a given tick
 * 1. Perform action
 * 2. Game updates
 * 3. State checks
 **/
let insertedCSS = false;
let canUseTime = false;
function gameLoop() {
  ri.tick = ri.tick.add(1);
  updateSpellCooldowns(tickLength);
  // Feature unlocks. These must happen every time the game is loaded.
  if (
    ri.activities.jog.unlocked ||
    ri.activities.meditate.unlocked ||
    !ri.activityQueue.some((q) => q.activity.name == 'rest') ||
    !ri.activityQueue.some((q) => q.activity.name == 'walk')
  ) {
    ri.unlockPanel('activities');
    ri.unlockPanel('activity-queue');
  }
  if (
    ri.distance.gte(25) ||
    ri.tick.gt(600) ||
    ri.activityQueue.reduce((acc, a) => acc + a.totalLength, 0) > 2 ||
    ri.activities.meditate.unlocked ||
    ri.activities.jog.unlocked
  ) {
    ri.unlockPanel('unlockables');
  }
  if (Object.values(ri.stats).some((stat) => stat.unlocked)) {
    ri.unlockPanel('stats');
    ri.unlockPerceptionMinigame();
  }
  if (Object.values(ri.spells).some((spell) => spell.unlocked)) {
    ri.unlockPanel('spells');
  }
  if (!canUseTime && ri.resources.time.unlocked) {
    const useSavedTimeToggle = ri.shadowRoot?.getElementById(
      'use-saved-time-toggle'
    );
    if (useSavedTimeToggle) {
      canUseTime = true;
      useSavedTimeToggle.hidden = false;
    }
  }
  // Option unlocks
  // Forest
  const gather = ri.activities.gather;
  const hunt = ri.activities.hunt;
  if (ri.distance.gte(1e3)) {
    if (!gather.options.includes('wood')) {
      gather.unlockOption('wood');
    }
  }
  // Hunt
  if (
    ri.stats.perception.value.gte(1.6) &&
    hunt.unlocked &&
    !hunt.options.includes('deer')
  ) {
    hunt.unlockOption('deer');
  }
  if (
    ri.stats.perception.value.gte(3) &&
    hunt.unlocked &&
    !hunt.options.includes('boar')
  ) {
    hunt.unlockOption('boar');
  }
  // Gather
  if (ri.stats.perception.value.gte(2) && !gather.options.includes('berry')) {
    gather.tryUnlock(true);
    gather.unlockOption('berry');
  }
  if (ri.distance.gte(100e3) && !gather.options.includes('bait')) {
    gather.tryUnlock(true);
    gather.unlockOption('bait');
  }
  if (
    !ri.activities.cook.options.includes('pemmican') &&
    ri.resources.meat.unlocked &&
    ri.resources.berry.unlocked
  ) {
    ri.activities.cook.tryUnlock(true);
    ri.activities.cook.unlockOption('pemmican');
  }

  // Landmarks. These only ever happen once.
  if (ri.distance.gte(ri.nextLandmark)) {
    if (ri.nextLandmark.equals(1e4)) {
      ri.log(
        'You have reached the forest! You see some movement in the trees, and a rabbit pops out.',
        ['persistent']
      );
      ri.activities.hunt.tryUnlock(true);
    } else if (ri.nextLandmark.equals(1e5)) {
      // ri.log('You have reached the arena. Fight for fun and profit!');
      // ri.activities.fight.tryUnlock(true);
    }
    ri.nextLandmark = ri.nextLandmark.times(10);
  }

  /* Determine this activity */ //TODO Refactor, this is so bad

  // If there're no activities to do, add a rest
  if (!ri.activityQueue.some((queueItem) => queueItem.repeat > 0)) {
    const restQueueItem = document.createElement(
      'ri-activity-queue-item'
    ) as ActivityQueueItem;
    restQueueItem.activity = ri.activities.rest;
    restQueueItem.repeat = 1;
    ri.activityQueue.push(restQueueItem);
  }

  // Get the current activity
  let queueItem = ri.activityQueue[ri.activityQueueIndex];
  // If we didn't find one, continue until we do
  while (!queueItem) {
    ri.activityQueueIndex += 1;
    ri.activityQueueIndex %= ri.activityQueue.length;
    queueItem.active = false;
    queueItem = ri.activityQueue[ri.activityQueueIndex];
    queueItem.active = true;
  }
  // check if it's out of repeats
  while (
    queueItem.activeIndex >= queueItem.totalLength ||
    queueItem.repeat == 0
  ) {
    // if so, move to the next one
    // queueItem.activeIndex = 0;
    ri.activityQueueIndex += 1;
    ri.activityQueueIndex %= ri.activityQueue.length;
    queueItem.active = false;
    queueItem = ri.activityQueue[ri.activityQueueIndex];
    queueItem.active = true;
    queueItem.activeIndex = 0;
  }
  ri.activeActivity = queueItem.activity as Activity;
  queueItem.activeIndex += 1;

  function resetLoopDelta() {
    // Restart queueLoopDelta tracking if we're at the beginning
    if (
      ri.activityQueueIndex == 0 &&
      (queueItem.activeIndex == 0 || queueItem.activeIndex == 1)
    ) {
      ri.activityQueue.forEach((queueItem, i) => {
        if (i !== ri.activityQueueIndex) queueItem.activeIndex = 0;
      });
      ri.lastQueueLoopDelta = ri.queueLoopDelta;
      ri.lastDistance = ri.distance;
      ri.lastResources = Object.fromEntries(
        Object.values(ri.resources).map((r) => [
          r.name,
          {
            value: r.value,
            capacity: r.capacity,
          },
        ])
      );
      ri.lastStats = Object.fromEntries(
        Object.values(ri.stats).map((s) => [
          s.name,
          {
            value: s.value,
          },
        ])
      );
    }
  }
  resetLoopDelta();

  /* Perform the active Activity */
  // TODO Clean this whole thing up

  // First, get the current step the activity is on
  let step = queueItem.activeIndex % queueItem.activity.length;
  const inProgress = step != 0;
  let performed = ri.activeActivity.perform(step, queueItem.optionIndex);
  if (!performed) queueItem.activeIndex -= 1;
  // If it's not performed, keep going until one is or we hit the start/end of the queue.
  let attempts = 1;
  while (
    !performed &&
    ri.activityQueueIndex != 0 &&
    attempts < ri.activityQueue.length
  ) {
    attempts += 1;
    // queueItem.activeIndex = 0;
    ri.activityQueueIndex += 1;
    ri.activityQueueIndex %= ri.activityQueue.length;
    queueItem.active = false;
    queueItem = ri.activityQueue[ri.activityQueueIndex];
    queueItem.active = true;
    queueItem.activeIndex = 0;
    if (ri.activityQueueIndex == 0) {
      resetLoopDelta();
    }
    // check if it's out of repeats
    while (
      queueItem.activeIndex >= queueItem.totalLength ||
      queueItem.repeat == 0
    ) {
      // if so, move to the next one
      // queueItem.activeIndex = 0;
      ri.activityQueueIndex += 1;
      ri.activityQueueIndex %= ri.activityQueue.length;
      queueItem = ri.activityQueue[ri.activityQueueIndex];
      queueItem.activeIndex = 0;
    }
    // Make it active and set its internal index
    ri.activeActivity = queueItem.activity as Activity;
    queueItem.activeIndex += 1;
    step = queueItem.activeIndex % queueItem.activity.length;
    performed = ri.activeActivity.perform(step, queueItem.optionIndex);
    if (!performed) queueItem.activeIndex -= 1;
  }
  // Completed!
  if (!inProgress && performed) {
    ri.activeActivity.uses = ri.activeActivity.uses.add(1);
    if (ri.lastCompletedActivity == ri.activeActivity) {
      ri.activeActivity.consecutiveUses =
        ri.activeActivity.consecutiveUses.add(1);
    } else {
      ri.activeActivity.consecutiveUses = new Decimal(1);
    }
    ri.lastCompletedActivity = ri.activeActivity;
  }

  updateUnlocks();

  // Do all Resource.prototype.onTick()s
  for (const resource of Object.values(ri.resources)) {
    resource.onTick(performed, ri.activeActivity);
  }

  updateUnlocks();

  /* Set attributes */

  for (const unlockable of ri.unlockables) {
    if (unlockable.unlocked && unlockable.hidden) {
      unlockable.hidden = false;
    }
  }
  for (const spell of Object.values(ri.spells)) {
    if (spell.unlocked) {
      spell.requestUpdate();
    }
  }

  /* Set resources gained this frame (deltas) */
  for (const oldStat of oldStats) {
    ri.stats[oldStat.name].delta = ri.stats[oldStat.name].value.minus(
      oldStat.value
    );
  }
  for (const oldResource of oldResources) {
    ri.resources[oldResource.name].delta = ri.resources[
      oldResource.name
    ].value.minus(oldResource.value);
  }
  /* Keep a copy of the current values to calculate deltas next time*/
  oldStats = (Object.values(ri.stats) as Stat[]).map((stat: Stat) => ({
    value: stat.value,
    name: stat.name,
  }));
  oldResources = (Object.values(ri.resources) as Resource[]).map(
    (resource: Resource) => ({
      value: resource.value,
      name: resource.name,
    })
  );

  // // Disabled for performance reasons.
  // ri.history.push({
  //   ...Object.fromEntries(
  //     Object.entries(ri.stats)
  //       .filter(([, v]) => v.unlocked)
  //       .map(([k, v]) => [k, [v.value]])
  //   ),
  //   ...Object.fromEntries(
  //     Object.entries(ri.resources)
  //       .filter(([k, v]) => v.unlocked)
  //       .map(([k, v]) => [k, [v.value, v.capacity]])
  //   ),
  //   distance: [ri.distance, 0],
  // });
  // ri.history = ri.history.slice(-600);

  const now = Date.now();
  if (lastSaved < now - 1 * 1000) {
    lastSaved = now;
    save();
  }
}

function updateUnlocks() {
  for (const unlockable of ri.unlockables) {
    if (unlockable.tryUnlock()) {
      ri.log(
        `Unlocked ${unlockable.displayName}, condition: ${unlockable.conditionString}`,
        ['unlockable', unlockable.name, 'persistent']
      );
    }
  }
}

function save() {
  if (!ri.saveP) return;
  /* Copy all save data to saveGame*/
  saveGame.version = saveVersion;
  saveGame.life = ri.life;
  saveGame.tick = ri.tick;
  saveGame.lastTime = lastTime;
  saveGame.lastSpellCooldownTime = lastSpellCooldownTime;
  saveGame.distance = ri.distance;
  saveGame.nextLandmark = ri.nextLandmark;
  saveGame.unlockedPanels = [...ri.unlockedPanels];
  saveGame.resources = Object.values(ri.resources).map(
    (resource: Resource) => ({
      name: resource.name,
      value: resource.value,
      baseCapacity: resource.baseCapacity,
      unlocked: resource.unlocked,
      secret: resource.secret,
      new: resource.new,
      hiddenByPlayer: resource.hiddenByPlayer,
    })
  );
  saveGame.activities = Object.values(ri.activities).map(
    (activity: Activity) => ({
      name: activity.name,
      uses: activity.uses,
      unlocked: activity.unlocked,
      secret: activity.secret,
      new: activity.new,
      hiddenByPlayer: activity.hiddenByPlayer,
    })
  );
  saveGame.spells = Object.values(ri.spells).map((spell: Spell) => ({
    name: spell.name,
    uses: spell.uses,
    unlocked: spell.unlocked,
    secret: spell.secret,
    new: spell.new,
    hiddenByPlayer: spell.hiddenByPlayer,
    currentCooldown: spell.currentCooldown.ceil(),
  }));
  saveGame.stats = Object.values(ri.stats).map((stat: Stat) => ({
    name: stat.name,
    value: stat.value,
    unlocked: stat.unlocked,
    secret: stat.secret,
    new: stat.new,
    hiddenByPlayer: stat.hiddenByPlayer,
  }));
  saveGame.loadouts = ri.loadouts.map((loadout: ActivityQueueItem[], i) => {
    if (ri.lastLoadoutIndex == i) {
      return ri.activityQueue.map((queueItem: ActivityQueueItem) => ({
        name: queueItem.activity.name,
        repeat: queueItem.repeat,
        activeIndex: queueItem.activeIndex,
        option: queueItem.option,
      }));
    }
    return loadout.map((queueItem) => ({
      name: queueItem.activity.name,
      repeat: queueItem.repeat,
      activeIndex: queueItem.activeIndex,
      option: queueItem.option,
    }));
  });
  saveGame.selectedLoadoutIndex = ri.lastLoadoutIndex;
  saveGame.activityQueueIndex = ri.activityQueueIndex;
  saveGame.log = ri.shortenLogEntries().map((logEntry) => {
    if (logEntry.msg) {
      return {
        msg: logEntry.msg,
        tags: Array.from(logEntry.tags),
        tick: logEntry.tick,
      };
    }
    if (logEntry.cost || logEntry.effect) {
      return {
        name: logEntry.name,
        displayName: logEntry.displayName,
        cost: [...logEntry.cost.entries()],
        effect: [...logEntry.effect.entries()],
        logString: logEntry.logString,
        tags: Array.from(logEntry.tags),
        tick: logEntry.tick,
      };
    }
  });
  saveGame.preferences = ri.preferences;
  // saveGame.history = ri.history.slice(-600);
  localStorage.setItem('saveGame', JSON.stringify(saveGame));
}

/**
 * The top element for runnerIdle.
 * Holds overarching gamestate and coordinates everything.
 *
 * @fires count-changed - Indicates when the count changes
 * @slot - This element has a slot
 * @csspart button - The button
 */
@customElement('runner-idle')
export class RunnerIdle extends LitElement {
  getActivityUsesByTag(tag: string) {
    return Object.values(this.activities)
      .filter((activity) => activity.tags.has(tag))
      .reduce((acc, activity) => acc.add(activity.uses), new Decimal(0));
  }
  lastCompletedActivity: Activity;
  unlockPerceptionMinigame() {
    document
      .getElementById('perception-click-target')!
      .removeAttribute('hidden');
  }
  @property({type: Decimal}) tick = new Decimal(0);
  gameSpeed = 1;
  preferences = {
    darkMode: false,
    showDetails: true,
    lightEffects: true,
    disablePerceptionMinigame: false,
  };
  distance = new Decimal(0);
  nextLandmark = new Decimal(1e4);
  unlockedPanels = new Set<string>();
  logEntries: LogEntry[] = [];
  get saveGame() {
    return saveGame;
  }

  lastResources: Record<string, {value: Decimal; capacity: Decimal}> = {};
  lastStats: Record<string, {value: Decimal}> = {};
  lastQueueLoopDelta: Record<
    string,
    {
      value: Decimal;
      tags: Set<string>;
      capacity?: Decimal;
      displayName?: string;
      perSecond: Decimal;
      timeToFullOrEmpty?: () => Decimal;
      unlocked?: boolean;
    }
  > = {};
  lastDistance: DecimalSource = 0;
  history: Record<string, {value: Decimal; capacity?: Decimal}>[] = [];

  get(name: string): IValuable {
    if (name.slice(-9) == ':capacity') {
      const resourceName = name.slice(0, -9);
      const item = this.get(resourceName);
      return {
        value: item.value,
        baseCapacity: item.baseCapacity,
        capacity: item.capacity,
        displayName: `${this.get(resourceName).displayName} capacity`,
        unlocked: item.unlocked,
        tags: item.tags,
      };
    }
    if (name === 'distance')
      return {value: this.distance, displayName: 'Distance', unlocked: true};
    if (name in this.resources) return this.resources[name];
    if (name in this.stats) return this.stats[name];
    // if (name in this.activities) return this.activities[name];
    throw new Error(`No such resource or stat: ${name}`);
  }

  set(name: string, value: DecimalSource): Decimal {
    if (name.slice(-9) == ':capacity') {
      const item = this.get(name.slice(0, -9));
      if (item.baseCapacity) {
        item.baseCapacity = new Decimal(value);
        return item.baseCapacity;
      }
      // if (item.capacity) {
      //   item.capacity = value;
      //   return item.capacity!;
      // }
      throw new Error(
        `${name} does not have a capacity property. Are you sure it's a Resource?`
      );
    }
    if (name === 'distance') {
      this.distance = new Decimal(value);
      return this.distance;
    }
    if (name in this.resources) {
      this.resources[name].value = new Decimal(value);
      return this.resources[name].value;
    }
    if (name in this.stats) {
      this.stats[name].value = new Decimal(value);
      return this.stats[name].value;
    }
    throw new Error(`No such resource or stat: ${name}`);
  }

  gain(name: string, amount: DecimalSource) {
    if (name.slice(-9) == ':capacity') {
      const item = this.get(name.slice(0, -9));
      if (item.baseCapacity) {
        item.baseCapacity = item.baseCapacity.plus(amount);
        return item.baseCapacity;
      }
      // if (item.capacity) {
      //   item.capacity = item.capacity.plus(amount);
      //   return item.capacity;
      // }
      throw new Error(
        `${name} does not have a capacity property. Are you sure it's a Resource?`
      );
    }
    const oldValue = this.get(name).value;
    this.set(name, oldValue.plus(amount));
    return amount;
  }

  lastQueueDeltaTime = new Decimal();
  get queueLoopDelta() {
    const lastTime = this.lastQueueDeltaTime;
    this.lastQueueDeltaTime = this.tick;
    if (
      Object.keys(this.lastResources).length == 0 ||
      Object.keys(this.lastStats).length == 0
    ) {
      return {};
    }
    const resourceDelta = Object.fromEntries(
      Object.values(this.resources).reduce(
        (acc: [string, object][], resource: Resource) => {
          const value = resource.value.minus(
            this.lastResources[resource.name].value
          );
          const perSecond = value.div(this.tick.minus(lastTime));

          return acc.concat([
            [
              resource.name,
              {
                value,
                tags: resource.tags,
                capacity: resource.capacity.minus(
                  this.lastResources[resource.name].capacity
                ),
                perSecond,
                timeToFullOrEmpty: () =>
                  perSecond.gt(0)
                    ? resource.vacancy.div(perSecond)
                    : perSecond.lt(0)
                    ? resource.value.div(-perSecond)
                    : new Decimal(0),
                unlocked: resource.unlocked,
              },
            ],
          ]);
        },
        []
      )
    );
    const statDelta = Object.fromEntries(
      Object.values(this.stats).reduce(
        (acc: [string, object][], stat: Stat) => {
          const value = stat.value.minus(this.lastStats[stat.name].value);
          const perSecond = value.div(this.tick.minus(lastTime));
          return acc.concat([
            [
              stat.name,
              {
                value,
                tags: stat.tags,
                unlocked: stat.unlocked,
                perSecond,
              },
            ],
          ]);
        },
        []
      )
    );
    return {
      distance: {
        displayName: 'Distance',
        tags: new Set(['distance']),
        unlocked: true,
        value: this.distance.minus(this.lastDistance),
        perSecond: this.distance
          .minus(this.lastDistance)
          .div(this.tick.minus(lastTime)),
      },
      ...resourceDelta,
      ...statDelta,
    };
  }

  activeActivity: Activity | null = null;
  activities: Record<string, Activity> = {};
  activityOptionsUnlockables: Record<string, UnlockableLitElement> = {};
  loadouts: ActivityQueueItem[][] = [[], [], [], [], []];
  lastLoadoutIndex = 0;
  activityQueue: ActivityQueueItem[] = this.loadouts[this.lastLoadoutIndex];
  activityQueueIndex = 0;

  resources: Record<string, Resource> = {};

  stats: Record<string, Stat> = {};

  spells: Record<string, Spell> = {};

  useSavedTime: Boolean = false;
  life = 0;

  get unlockables() {
    return [
      ...Object.values(this.activities),
      ...Object.values(this.resources),
      ...Object.values(this.stats),
      ...Object.values(this.spells),
      ...Object.values(this.activityOptionsUnlockables),
    ] as UnlockableLitElement[];
  }

  logCostEffect(
    activity: {name: string; displayName: string},
    logString: string,
    cost: Cost,
    effect: Cost,
    tags: Set<string> | string[] = new Set()
  ) {
    const lastEntry = this.logEntries[0] as ActivityLogEntry;
    const mergeID = activity.name;
    if (lastEntry?.mergeID === mergeID) {
      lastEntry.add({
        cost,
        effect,
      });

      return;
    }
    const activityLogEntry = document.createElement(
      'ri-activity-log-entry'
    ) as ActivityLogEntry;
    activityLogEntry.name = activity.name;
    activityLogEntry.displayName = activity.displayName;
    activityLogEntry.cost = cost;
    activityLogEntry.effect = effect;
    activityLogEntry.datetime = Date.now();
    activityLogEntry.tick = this.tick;
    activityLogEntry.life = this.life;
    activityLogEntry.mergeID = mergeID;
    activityLogEntry.tags = new Set([...tags, activity.name]);
    // TODO Dedupe this
    this.logEntries.unshift(activityLogEntry);
    if (this.logEntries.length > 1000) {
      this.shortenLogEntries();
    }
    // this.requestUpdate();
    this.shadowRoot?.querySelector('ri-log')?.requestUpdate();
    (document.querySelector('ri-settings') as Settings).requestUpdate();
  }
  log(msg: string, tags: Set<string> | string[] = new Set()) {
    if (Array.isArray(tags)) {
      // tags = new Set(tags.map((tag) => tag.toLowerCase()));
      tags = new Set(tags);
    }
    const logEntry = document.createElement('ri-log-entry') as LogEntry;
    logEntry.msg = msg;
    logEntry.datetime = Date.now();
    logEntry.tick = this.tick;
    logEntry.life = this.life;
    logEntry.tags = tags;
    this.logEntries.unshift(logEntry);
    if (this.logEntries.length > 1000) {
      this.shortenLogEntries();
    }
    // this.requestUpdate();
    this.shadowRoot?.querySelector('ri-log')?.requestUpdate();
    (document.querySelector('ri-settings') as Settings).requestUpdate();
  }

  shortenLogEntries() {
    return this.logEntries
      .filter((entry) => entry.tags.has('persistent'))
      .slice(0, 500);
  }

  /** Relates grid-areas to indices for placement during unlockPanel */
  unlockPanelMap: Record<string, [x: number, y: number][]> = {
    world: [[3, 2]],
    activities: [[3, 1]],
    stats: [[1, 0]],
    'activity-queue': [
      [1, 1],
      [2, 1],
    ],
    'queue-loop-delta': [
      [3, 0],
      [4, 0],
    ],
    unlockables: [
      [1, 2],
      [2, 2],
      [3, 2],
      [4, 2],
    ],
    spells: [
      // [3, 2],
      [4, 2],
    ],
  };
  /** Unlocks a game feature panel */
  unlockPanel(panelId: string) {
    const panel = this.shadowRoot?.getElementById(panelId);
    if (panel?.classList.contains('locked')) {
      panel.classList.remove('locked');
      if (!this.unlockedPanels.has(panelId)) {
        panel.classList.add('new');
        panel.addEventListener('mouseover', () => {
          panel.classList.remove('new');
        });
        this.unlockedPanels.add(panelId);
      }
      const currentGridTemplateAreas =
        window.getComputedStyle(this).gridTemplateAreas;
      const GTAArray: string[][] = currentGridTemplateAreas
        .split('" "')
        .map((arr) => arr.split(' '));
      if (GTAArray.length == 5) {
        for (const [row, col] of this.unlockPanelMap[panelId]) {
          let finalPanelId = panelId;
          if (col == 0 && row == 0) finalPanelId = `"${finalPanelId}`;
          if (col == GTAArray[0].length && row == GTAArray.length)
            finalPanelId = `${panelId}"`;
          GTAArray[row][col] = finalPanelId;
        }
        this.style.gridTemplateAreas = GTAArray.map((arr) =>
          arr.join(' ')
        ).join('" "');
      }
    }
  }

  saveP = true;
  resetSave() {
    localStorage.removeItem('saveGame');
    this.saveP = false;
    window.location.reload();
  }

  constructor() {
    super();
    setTimeout(this.focus.bind(this));

    this.init();
    if (saveGame == null) {
      saveGame = {};
      // setTimeout(() => tutorial(this), 0);
    } else {
      this.load(saveGame);
    }

    gameLoop();
    StartGameLoop();
  }

  init() {
    /** ADD RESOURCES **/
    // Note if you're bughunting:
    // Caution! value has a setter that clamps to [0, capacity],
    // so capacity assignments must happen before value assignments
    // or the value can be clamped to the prior capacity.
    // Object.assign appears to use this order,
    // but this is sketchy voodoo.
    // SKETCHY FREAKING VOODOO. We are in the land of undefined behaviour and it is BAD.
    // TODO: Refactor (Fix, honestly.) to use arrays? Make a class for clamped values directly?

    this.resources.health = document.createElement('ri-resource') as Resource;
    this.resources.health.classList.add('health');
    Object.assign(this.resources.health, {
      name: 'health',
      displayName: 'Health',
      description:
        'Your health.<h2 style="margin:0px;color:orangered;"> If it reaches 0, you die!</h2>But death is not the end...',
      tags: ['body'],
      capacity: new Decimal(10),
      value: new Decimal(10),
      onMinimum: () =>
        this.log(
          `<span class='danger'>You died.</span> Currently not implemented.`,
          ['death']
        ),
      conditionString: 'Take damage for the first time',
      condition: () =>
        this.resources.health.value.lt(this.resources.health.capacity),
      visibleCondition: () =>
        this.resources.health.value.lt(this.resources.health.capacity),
    });

    this.resources.healthExp = document.createElement(
      'ri-resource'
    ) as Resource;
    this.resources.healthExp.classList.add('healthExp');
    Object.assign(this.resources.healthExp, {
      name: 'healthExp',
      displayName: 'Health XP',
      description:
        "<strong>只要不被杀死，你就会越来越强大。</strong><br>生命值不足时可以触发。<br>在危险时提高生命值。",
      tags: ['body', 'experience'],
      capacity: new Decimal(10),
      onMaxCapacity: () => {
        this.resources.healthExp._value = this.resources.healthExp._value.minus(
          this.resources.healthExp.capacity
        );
        const oldCapacity = this.resources.health.capacity;
        this.resources.health.capacity = this.resources.health.capacity.plus(1);
        this.log(
          `Increase Health Capacity by ${this.resources.health.capacity.minus(
            oldCapacity
          )}!`,
          ['health', 'capacity']
        );
        this.resources.healthExp.capacity = this.resources.healthExp.capacity
          .times(1.1)
          .floor();
      },
      onTick: () => {
        // HealthExp gain per second based on current health
        // "What doesn't kill you makes you stronger"
        if (this.resources.health.value.lte(this.resources.health.capacity)) {
          // this.resources.healthExp = this.resources.health.capacity - this.resources.health.value
          const healthExpGained = this.resources.health.vacancy.div(100);
          this.resources.healthExp.value =
            this.resources.healthExp.value.plus(healthExpGained);
          // logResource('healthExp', healthExpGained);
        }
      },
      value: new Decimal(0),
      secret: true,
      conditionString: 'Unlock Health',
      condition: () => this.resources.health.unlocked,
      visibleCondition: () => this.resources.health.unlocked,
    });

    this.resources.nutrition = document.createElement(
      'ri-resource'
    ) as Resource;
    this.resources.nutrition.classList.add('nutrition');
    Object.assign(this.resources.nutrition, {
      name: 'nutrition',
      displayName: 'Satiation',
      description: `Food fills you up! Goes down by 1 point every 5 seconds at base.
      <br>Using Stamina uses more.
      <br>There are no benefits or downsides to Satiation (for now),
      <br>it just limits how much food you can eat.`,
      tags: ['body', 'food'],
      capacity: new Decimal(100),
      value: new Decimal(0),
      onTick: () => {
        // Hunger
        if (this.tick.toNumber() % 5 == 0)
          this.resources.nutrition.value =
            this.resources.nutrition.value.minus(1);
      },
      onMinimum: () => {
        // this.log("You're starving! Find some food, quick!");
        // this.gain('health', -1);
        // TODO add ascetic/masochistic rewards for hunger? monk stuff?
        // TODO!!!! Karma as a hidden multi-life luck element
      },
      secret: true,
      conditionString: `Find some food`,
      condition: () =>
        Object.values(this.resources).some(
          (r) => r.tags.has('food') && r.unlocked
        ),
      visibleCondition: () =>
        Object.values(this.resources).some(
          (r) => r.tags.has('food') && r.unlocked
        ),
    });

    this.resources.wood = document.createElement('ri-resource') as Resource;
    this.resources.wood.classList.add('wood');
    Object.assign(this.resources.wood, {
      name: 'wood',
      displayName: 'Wood',
      description:
        '<span class="fancy wood">Wood</span> is used to build things.<br>You can find <span class="fancy wood">Wood</span> in the forest (among other places).',
      tags: ['wood', 'raw'],
      baseCapacity: new Decimal(10),
      value: new Decimal(0),
      secret: true,
      conditionString: 'Gather Wood',
      condition: () => this.resources.wood.value.gt(0),
      visibleCondition: () =>
        this.activities.gather.unlocked &&
        this.activities.gather.options.includes('wood'),
    });
    Object.defineProperty(this.resources.wood, 'capacity', {
      get: function () {
        return this.baseCapacity.times(
          Math.max(ri.stats.spaceMagic.value.log10(), 1)
        );
      },
      set: function (value) {
        this.baseCapacity = value;
      },
    });

    this.resources.meat = document.createElement('ri-resource') as Resource;
    this.resources.meat.classList.add('meat');
    Object.assign(this.resources.meat, {
      name: 'meat',
      displayName: 'Meat',
      description:
        '<span class="fancy meat">Meat</span>, raw and bloody. I guess you could eat this?',
      tags: ['food', 'raw'],
      baseCapacity: new Decimal(25),
      value: new Decimal(0),
      secret: true,
      conditionString: `Get Meat`,
      condition: () => this.resources.meat.value.gt(0),
      visibleCondition: () => this.resources.meat.value.gt(0),
    });
    Object.defineProperty(this.resources.meat, 'capacity', {
      get: function () {
        return this.baseCapacity.times(
          Math.max(ri.stats.spaceMagic.value.log10(), 1)
        );
      },
      set: function (value) {
        this.baseCapacity = value;
      },
    });

    this.resources.fish = document.createElement('ri-resource') as Resource;
    this.resources.fish.classList.add('fish');
    Object.assign(this.resources.fish, {
      name: 'fish',
      displayName: 'Fish',
      description: `<span class="fancy fish">Fish</span>, raw and fishy. I guess you could eat this?
        <br>I'm just kidding, it's delicious.`,
      tags: ['food', 'raw'],
      baseCapacity: new Decimal(100),
      value: new Decimal(0),
      secret: true,
      conditionString: `Get Fish`,
      condition: () => this.resources.fish.value.gt(0),
      visibleCondition: () => this.resources.fish.value.gt(0),
    });
    Object.defineProperty(this.resources.fish, 'capacity', {
      get: function () {
        return this.baseCapacity.times(
          Math.max(ri.stats.spaceMagic.value.log10(), 1)
        );
      },
      set: function (value) {
        this.baseCapacity = value;
      },
    });

    this.resources.bait = document.createElement('ri-resource') as Resource;
    this.resources.bait.classList.add('bait');
    Object.assign(this.resources.bait, {
      name: 'bait',
      displayName: 'Bait',
      description: `<span class="fancy bait">Bait</span>, raw and baity.
      <br>${fancy('fish', 'Fish')} can't get enough of the stuff.`,
      tags: [],
      baseCapacity: new Decimal(100),
      value: new Decimal(0),
      secret: true,
      conditionString: `Get bait`,
      condition: () => this.resources.bait.value.gt(0),
      visibleCondition: () => this.resources.bait.value.gt(0),
    });
    Object.defineProperty(this.resources.bait, 'capacity', {
      get: function () {
        return this.baseCapacity.times(
          Math.max(ri.stats.spaceMagic.value.log10(), 1)
        );
      },
      set: function (value) {
        this.baseCapacity = value;
      },
    });

    this.resources.cookedMeat = document.createElement(
      'ri-resource'
    ) as Resource;
    this.resources.cookedMeat.classList.add('cookedMeat');
    Object.assign(this.resources.cookedMeat, {
      name: 'cookedMeat',
      displayName: 'Cooked Meat',
      description:
        'A well-cooked (and not well-done) piece of <span class="fancy cookedMeat">Cooked Meat</span>. Much better than the raw stuff.',
      tags: ['food', 'cooked'],
      baseCapacity: new Decimal(25),
      value: new Decimal(0),
      secret: true,
      conditionString: `Make it medium-rare`,
      condition: () => this.resources.cookedMeat.value.gt(0),
      visibleCondition: () => this.resources.meat.value.gt(0),
    });
    Object.defineProperty(this.resources.cookedMeat, 'capacity', {
      get: function () {
        return this.baseCapacity.times(
          Math.max(ri.stats.spaceMagic.value.log10(), 1)
        );
      },
      set: function (value) {
        this.baseCapacity = value;
      },
    });

    this.resources.pemmican = document.createElement('ri-resource') as Resource;
    this.resources.pemmican.classList.add('pemmican');
    Object.assign(this.resources.pemmican, {
      name: 'pemmican',
      displayName: 'Pemmican',
      description:
        'A packed together mixture of ground-up dried <span class="fancy meat">Meat</span>, fat, and mystery <span class="fancy berry">Berries</span>.<br>Very nutritious!',
      tags: ['food', 'cooked'],
      baseCapacity: new Decimal(100),
      secret: true,
      conditionString: `Mix some meat and berries`,
      condition: () => this.resources.pemmican.value.gt(0),
      visibleCondition: () =>
        this.resources.meat.unlocked && this.resources.berry.unlocked,
    });
    Object.defineProperty(this.resources.pemmican, 'capacity', {
      get: function () {
        return this.baseCapacity.times(
          Math.max(ri.stats.spaceMagic.value.log10(), 1)
        );
      },
      set: function (value) {
        this.baseCapacity = value;
      },
    });

    this.resources.manameat = document.createElement('ri-resource') as Resource;
    this.resources.manameat.classList.add('manameat');
    Object.assign(this.resources.manameat, {
      name: 'manameat',
      displayName: 'Manameat',
      tags: ['food'],
      baseCapacity: new Decimal(10),
      value: new Decimal(0),
      secret: true,
      conditionString: `Find Manameat`,
      condition: () => this.resources.manameat.value.gt(0),
      visibleCondition: () => this.resources.manameat.value.gt(0),
    });
    Object.defineProperty(this.resources.manameat, 'capacity', {
      get: function () {
        return this.baseCapacity.times(
          Math.max(ri.stats.spaceMagic.value.log10(), 1)
        );
      },
      set: function (value) {
        this.baseCapacity = value;
      },
    });

    this.resources.berry = document.createElement('ri-resource') as Resource;
    this.resources.berry.classList.add('berry');
    Object.assign(this.resources.berry, {
      name: 'berry',
      displayName: 'Berries',
      tags: ['food'],
      description: `Some wild <span class="fancy berry">Berries</span>. Nutritious and delicious!<br>They make you feel a little strange.`,
      baseCapacity: new Decimal(100),
      value: new Decimal(0),
      secret: true,
      conditionString: `Find Berries (${fancy(
        'perception',
        'PER'
      )}: 2 and Gather)`,
      condition: () => this.resources.berry.value.gt(0),
      visibleCondition: () => this.stats.perception.value.gt(1),
    });
    Object.defineProperty(this.resources.berry, 'capacity', {
      get: function () {
        return this.baseCapacity.times(
          Math.max(ri.stats.spaceMagic.value.log10(), 1)
        );
      },
      set: function (value) {
        this.baseCapacity = value;
      },
    });

    // Stamina
    this.resources.stamina = document.createElement('ri-resource') as Resource;
    this.resources.stamina.classList.add('stamina');
    Object.assign(this.resources.stamina, {
      name: 'stamina',
      displayName: 'Stamina',
      description:
        'The energy your body uses for physical activity.<br>Restore by <span class="fancy rest">Resting</span>.',
      tags: ['body', 'train'],
      baseCapacity: new Decimal(1),
      value: new Decimal(1),
      beforeGain: (_newValue: Decimal) => {
        ri.resources.staminaExp.value = ri.resources.staminaExp.value.plus(
          _newValue
            .min(ri.resources.stamina.capacity)
            .minus(ri.resources.stamina.value)
        );
      },
      beforeLoss: (_newValue: Decimal) => {
        ri.resources.nutrition.value = ri.resources.nutrition.value.minus(
          1
          // ri.resources.stamina.value.minus(_newValue.max(0)).div(10)
        );
      },
    });
    Object.defineProperty(this.resources.stamina, 'capacity', {
      get: function () {
        return this.baseCapacity.times(
          ri.stats.endurance.value.times(10).log10()
        );
      },
      set: function (value) {
        this.baseCapacity = value;
      },
    });

    //Stamina XP
    this.resources.staminaExp = document.createElement(
      'ri-resource'
    ) as Resource;
    this.resources.staminaExp.classList.add('staminaExp');
    Object.assign(this.resources.staminaExp, {
      name: 'staminaExp',
      displayName: 'Stamina XP',
      description:
        'Gain Stamina XP by restoring your Stamina.<br>Stamina capacity increases on max XP.',
      tags: ['body', 'train', 'experience', 'hide-effect'],
      capacity: new Decimal(5),
      value: new Decimal(0),
      onMaxCapacity: () => {
        this.resources.staminaExp._value =
          this.resources.staminaExp._value.minus(
            this.resources.staminaExp.capacity
          );
        const oldCapacity = this.resources.stamina.baseCapacity;
        // Add 10% but at least +1 Stamina
        this.resources.stamina.capacity = oldCapacity
          .times(1.1)
          .floor()
          .max(oldCapacity.plus(1));
        this.log(
          `<strong>Increase Stamina Capacity by ${this.resources.stamina.capacity.minus(
            oldCapacity
          )}!</strong>`,
          ['stamina', 'capacity']
        );
        // Add 30% but at least +1 Stamina XP
        this.resources.staminaExp.capacity = this.resources.staminaExp.capacity
          .times(1.3)
          .floor()
          .max(this.resources.staminaExp.capacity.plus(1));
      },
    });

    // Focus
    this.resources.focus = document.createElement('ri-resource') as Resource;
    this.resources.focus.classList.add('focus');
    Object.assign(this.resources.focus, {
      name: 'focus',
      displayName: 'Focus',
      description: `In the ZONE. Increases Training effectiveness.<br>Push your limits to improve.`,
      tags: ['body', 'train'],
      capacity: new Decimal(100),
      value: new Decimal(0),
      onTick: (performed: boolean, activity: Activity) => {
        if (performed && activity.tags.has('train')) {
          // Gain focus capacity by training at max focus
          if (ri.resources.focus.value.gte(ri.resources.focus.capacity)) {
            ri.resources.focus.capacity = ri.resources.focus.capacity.plus(0.1);
          }
          ri.gain('focus', 1);
        } else {
          // Focus drops by 1/s when not training
          ri.resources.focus.value = ri.resources.focus.value.minus(1).max(0);
        }
      },
      conditionString: 'Train 20 times',
      condition: () => sumActivitiesByTag('train', 'uses').gte(20),
      progress: () => sumActivitiesByTag('train', 'uses').div(20),
      // Visible if some 'train' activity is unlocked
      visibleCondition: () =>
        Object.values(this.activities).some(
          (activity: Activity) =>
            activity.tags.has('train') && activity.unlocked
        ),
    });

    this.resources.mana = document.createElement('ri-resource') as Resource;
    this.resources.mana.classList.add('mana');
    Object.assign(this.resources.mana, {
      name: 'mana',
      displayName: 'Mana',
      description: 'Magical energy. Used for Spells.',
      tags: ['mind', 'meditate'],
      baseCapacity: new Decimal(1),
      value: new Decimal(0),
      beforeGain: (_newValue: Decimal) => {
        ri.resources.manaExp.value = ri.resources.manaExp.value.plus(
          _newValue.minus(ri.resources.mana.value)
        );
      },
      secret: true,
      conditionString: 'Get Mana',
      condition: () => this.resources.mana.value.gt(0),
      visibleCondition: () => this.resources.mana.value.gt(0),
      persistentUnlock: true,
    });
    Object.defineProperty(this.resources.mana, 'capacity', {
      get: function () {
        return this.baseCapacity.times(ri.stats.wisdom.value.times(10).log10());
      },
      set: function (value) {
        this.baseCapacity = value;
      },
    });

    // Mana XP
    this.resources.manaExp = document.createElement('ri-resource') as Resource;
    this.resources.manaExp.classList.add('manaExp');
    Object.assign(this.resources.manaExp, {
      name: 'manaExp',

      displayName: 'Mana XP',
      description:
        'Gain Mana XP by gaining Mana.<br>Mana capacity increases on max XP.',
      tags: ['mind', 'meditate', 'experience'],
      capacity: new Decimal(10),
      onMaxCapacity: () => {
        this.resources.manaExp._value = this.resources.manaExp._value.minus(
          this.resources.manaExp.capacity
        );
        const oldCapacity = this.resources.mana.baseCapacity;
        this.resources.mana.baseCapacity = oldCapacity.plus(1);
        this.log(
          `Increase Mana Capacity by ${this.resources.mana.capacity.minus(
            oldCapacity
          )}!`,
          ['mana', 'capacity']
        );
        this.resources.manaExp.capacity = this.resources.manaExp.capacity
          .times(1.5)
          .floor();
      },
      value: new Decimal(0),
      secret: true,
      conditionString: 'Unlock Mana',
      condition: () => this.resources.mana.unlocked,
      visibleCondition: () =>
        this.resources.mana.unlocked || this.resources.manaDepth.unlocked,
      persistentUnlock: true,
    });

    // Mana Depth
    this.resources.manaDepth = document.createElement(
      'ri-resource'
    ) as Resource;
    this.resources.manaDepth.classList.add('manaDepth');
    Object.assign(this.resources.manaDepth, {
      name: 'manaDepth',
      displayName: 'Mana Density',
      description: `The density of the Mana around you. Improves the potential of Meditation.<br>Increases while Meditating and goes down otherwise.`,
      tags: ['mind', 'meditate'],
      capacity: new Decimal(1e3),
      value: new Decimal(0),
      onTick: (performed: boolean, activity: Activity) => {
        // Doing meditation activities adds manaDepth, which depletes otherwise
        if (performed && activity.tags.has('meditate')) {
          ri.gain('manaDepth', 1);
        } else {
          ri.resources.manaDepth.value = ri.resources.manaDepth.value
            .times(0.9)
            .floor();
        }
      },

      secret: true,
      conditionString: 'Meditate 10 times in a row',
      condition: () => this.activities.meditate.consecutiveUses.gte(10),
      progress: () => this.activities.meditate.consecutiveUses.div(10),
      visibleCondition: () => this.activities.meditate.unlocked,
      persistentUnlock: true,
    });

    // // Sanity
    // this.resources.sanity = document.createElement('ri-resource') as Resource;
    // this.resources.sanity.classList.add('sanity');
    // Object.assign(this.resources.sanity, {
    //   name: 'sanity',
    //   displayName: 'Sanity',
    //   tags: ['mind'],
    //   capacity: new Decimal(10),
    //   value: new Decimal(10),
    //   secret: true,
    //   conditionString: 'Go insane',
    //   condition: () => this.resources.sanity.value.equals(0),
    //   visibleCondition: () => this.resources.sanity.value.equals(0),
    //   persistentUnlock: true,
    //   persistentValue: true,
    // });

    // Time
    this.resources.time = document.createElement('ri-resource') as Resource;
    this.resources.time.classList.add('time');
    Object.assign(this.resources.time, {
      name: 'time',
      displayName: 'Time',
      description: `Bottled Time! The basic component of all Time Magic.<br><br>Ironically, smells like oregano.`,
      tags: ['special'],
      capacity: new Decimal(1800),
      value: new Decimal(0),
      valueFormatter: formatTime,
      capacityFormatter: formatTime,
      onUnlock: () => {},
      secret: true,
      conditionString: 'Save 60 seconds of Time',
      condition: () => this.resources.time.value.gte(60),
      progress: () => this.resources.time.value.div(60),
      visibleCondition: () => this.resources.time.value.gte(1),
      persistentUnlock: true,
    });

    // Light
    this.resources.light = document.createElement('ri-resource') as Resource;
    this.resources.light.classList.add('light');
    Object.assign(this.resources.light, {
      name: 'light',
      displayName: 'Light',
      description: `How bright it is in your immediate vicinity.
        <br>0 represents the normal ambient light around you.
        <br>-100 is complete darkness,
        <br>100 is extremely bright.`,
      tags: ['special'],
      capacity: new Decimal(100),
      minimum: new Decimal(-100),
      value: new Decimal(0),
      onTick: () => {
        // Light moves towards 0 over time
        this.resources.light.value = this.resources.light.value.minus(
          this.resources.light.value.sign()
        );
      },
      secret: true,
      conditionString: 'Change the light level',
      condition: () => !this.resources.light.value.eq(0),
      visibleCondition: () => this.resources.light.unlocked,
    });

    // Fire
    this.resources.fire = document.createElement('ri-resource') as Resource;
    this.resources.fire.classList.add('fire');
    Object.assign(this.resources.fire, {
      name: 'fire',
      displayName: 'Fire',
      description: `Fire! Keep it burning and it'll keep growing, but stop and it'll go out!`,
      tags: ['special'],
      capacity: new Decimal(100),
      value: new Decimal(0),
      onTick: () => {
        const fire = this.resources.fire;
        const wood = this.resources.wood;
        // Grow/shrink the fire capacity based on current fullness
        if (fire.value.gt(fire.capacity.div(2))) {
          fire.capacity = fire.capacity.plus(fire.value.div(10));
        } else if (fire.capacity.gt(10)) {
          fire.capacity = fire.capacity.minus(fire.vacancy.div(10));
        }
        // Let off some Light
        if (Math.random() < 0.01 * fire.value.toNumber()) {
          this.resources.light.value = this.resources.light.value.plus(
            fire.value.div(10).floor()
          );
        }
        // sometimes burn some wood
        if (wood.value.gt(0)) {
          if (Math.random() < 0.01 * fire.value.toNumber()) {
            wood.value = wood.value.minus(1);
            fire.capacity = Decimal.max(fire.capacity, fire.value.plus(10));
            fire.value = fire.value.plus(10);
          }
        }
        fire.value = fire.value.minus(fire.value.times(0.05).max(0.001));
      },
      secret: true,
      conditionString: 'Light a fire',
      condition: () => this.resources.fire.value.gt(0),
      visibleCondition: () => this.resources.fire.unlocked,
    });

    // // Mana Light
    // this.resources.manaLight = document.createElement(
    //   'ri-resource'
    // ) as Resource;
    // this.resources.manaLight.classList.add('manaLight');
    // Object.assign(this.resources.manaLight, {
    //   name: 'manaLight',Currently the game seems to be having some rendering issues causing lag, especially after long play sessions. If you're having this issue, I'd recommend refreshing the page temporarily, but it also
    //   displayName: 'Magic Lightbulb',
    //   description: 'Create light from mana.',
    //   capacity: new Decimal(100),
    //   value: new Decimal(0),
    //   onTick: () => {
    //     this.resources.light.value = this.resources.light.value.plus(
    //       this.resources.manaLight.value
    //     );
    //     this.resources.mana.value = this.resources.mana.value.minus(
    //       this.resources.manaLight.value
    //     );
    //   },
    // });

    // // Solar Panel
    // this.resources.solarPanel = document.createElement(
    //   'ri-resource'
    // ) as Resource;
    // this.resources.solarPanel.classList.add('solarPanel');
    // Object.assign(this.resources.solarPanel, {
    //   name: 'solarPanel',
    //   displayName: 'Magic Solar Panel',
    //   description: 'Absorb light to create mana',
    //   capacity: new Decimal(100),
    //   value: new Decimal(0),
    //   onTick: () => {
    //     this.resources.light.value = this.resources.light.value.minus(
    //       this.resources.solarPanel.value
    //     );
    //     this.resources.mana.value = this.resources.mana.value.plus(
    //       this.resources.solarPanel.value
    //     );
    //   },
    // });

    /** ADD STATS **/

    // TODO Control, unlockable ability to act like you have lower stats
    this.stats.speed = document.createElement('ri-stat') as Stat;
    this.stats.speed.classList.add('speed');
    Object.assign(this.stats.speed, {
      name: 'speed',
      displayName: 'Speed',
      shortName: 'SPD',
      value: new Decimal(1),
      description:
        "Increases <span class='fancy distance'>Distance</span> traveled per <strong>second</strong>, but doesn't change distance per <strong>stamina</strong>.",
      conditionString: 'Gain some Speed',
      condition: () => this.stats.speed.value.gt(1),
      visibleCondition: () => this.stats.speed.value.gt(1),
    });

    this.stats.strength = document.createElement('ri-stat') as Stat;
    this.stats.strength.classList.add('strength');
    Object.assign(this.stats.strength, {
      name: 'strength',
      displayName: 'Strength',
      shortName: 'STR',
      description: 'For many things, reduces Stamina cost',
      value: new Decimal(1),
      conditionString: 'Gain some Strength',
      condition: () => this.stats.strength.value.gt(1),
      visibleCondition: () => this.stats.strength.value.gt(1),
    });

    this.stats.constitution = document.createElement('ri-stat') as Stat;
    this.stats.constitution.classList.add('constitution');
    Object.assign(this.stats.constitution, {
      name: 'constitution',
      displayName: 'Constitution',
      shortName: 'CON',
      description:
        'Bounce back faster. Improves how much you can heal and restore yourself per second.',
      value: new Decimal(1),
      secret: true,
      conditionString: 'Gain some Consitution',
      condition: () => this.stats.constitution.value.gt(1),
      visibleCondition: () => this.stats.constitution.value.gt(1),
    });

    this.stats.endurance = document.createElement('ri-stat') as Stat;
    this.stats.endurance.classList.add('endurance');
    Object.assign(this.stats.endurance, {
      name: 'endurance',
      displayName: 'Endurance',
      shortName: 'END',
      description:
        'Tough it out. Improves how much you can work before needing to rest.',
      value: new Decimal(1),
      secret: true,
      conditionString: 'Gain some Endurance',
      condition: () => this.stats.endurance.value.gt(1),
      visibleCondition: () => this.stats.endurance.value.gt(1),
    });

    this.stats.wisdom = document.createElement('ri-stat') as Stat;
    this.stats.wisdom.classList.add('wisdom');
    Object.assign(this.stats.wisdom, {
      name: 'wisdom',
      displayName: 'Wisdom',
      shortName: 'WIS',
      description:
        "This isn't D&D. You're a wizard, be wise! .<br>Increases Mana",
      value: new Decimal(1),
      beforeChange: (newValue: Decimal) =>
        this.resources.mana.capacity.plus(
          newValue.minus(this.stats.wisdom.value)
        ),
      secret: true,
      conditionString: 'Gain some Wisdom',
      condition: () => this.stats.wisdom.value.gt(1),
      visibleCondition: () => this.stats.wisdom.value.gt(1),
      // persistentUnlock: true,
      persistentValue: true,
    });

    this.stats.perception = document.createElement('ri-stat') as Stat;
    this.stats.perception.classList.add('perception');
    Object.assign(this.stats.perception, {
      name: 'perception',
      displayName: 'Perception',
      shortName: 'PER',
      description:
        'See more better. Find secrets to improve.<br>Improves Hunting and Gathering accuracy.',
      value: new Decimal(1),
      secret: true,
      conditionString: 'Click a secret!',
      condition: () => this.stats.perception.value.gt(1),
      visibleCondition: () => this.stats.perception.value.gt(1),
      persistentUnlock: true,
      persistentValue: true,
    });

    this.stats.intelligence = document.createElement('ri-stat') as Stat;
    this.stats.intelligence.classList.add('intelligence');
    Object.assign(this.stats.intelligence, {
      name: 'intelligence',
      displayName: 'Intelligence',
      shortName: 'INT',
      description:
        'Think good-like.<br>Needed for unlocking some spells. More coming.',
      value: new Decimal(1),
      secret: true,
      conditionString: 'Gain some intelligence',
      condition: () => this.stats.intelligence.value.gt(1),
      visibleCondition: () => this.stats.intelligence.value.gt(1),
    });

    this.stats.lightMagic = document.createElement('ri-stat') as Stat;
    this.stats.lightMagic.classList.add('lightMagic');
    Object.assign(this.stats.lightMagic, {
      name: 'lightMagic',
      displayName: 'Light Magic',
      shortName: 'LGT',
      description: 'Manipulate light.',
      min: new Decimal(0),
      value: new Decimal(0),
      secret: true,
      conditionString: 'Cast a <span class="fancy light">Light</span> spell',
      condition: () => this.stats.lightMagic.value.gt(0),
      visibleCondition: () =>
        Object.values(this.spells)
          .filter((spell) => spell.tags.has('lightMagic'))
          .some((spell) => spell.unlocked),
    });

    this.stats.fireMagic = document.createElement('ri-stat') as Stat;
    this.stats.fireMagic.classList.add('fireMagic');
    Object.assign(this.stats.fireMagic, {
      name: 'fireMagic',
      displayName: 'Fire Magic',
      shortName: 'FIR',
      description: 'Manipulate fire.',
      min: new Decimal(0),
      value: new Decimal(0),
      secret: true,
      conditionString: 'Cast a <span class="fancy fire">Fire</span> spell',
      condition: () => this.stats.fireMagic.value.gt(0),
      visibleCondition: () =>
        Object.values(this.spells)
          .filter((spell) => spell.tags.has('fireMagic'))
          .some((spell) => spell.unlocked),
    });

    this.stats.timeMagic = document.createElement('ri-stat') as Stat;
    this.stats.timeMagic.classList.add('timeMagic');
    Object.assign(this.stats.timeMagic, {
      name: 'timeMagic',
      displayName: 'Time Magic',
      shortName: 'TIM',
      description: 'Manipulate time.',
      min: new Decimal(0),
      value: new Decimal(0),
      secret: true,
      conditionString: 'Cast a <span class="fancy time">Time</span> spell',
      condition: () => this.stats.timeMagic.value.gt(0),
      visibleCondition: () =>
        Object.values(this.spells)
          .filter((spell) => spell.tags.has('timeMagic'))
          .some((spell) => spell.unlocked),
    });

    this.stats.spaceMagic = document.createElement('ri-stat') as Stat;
    this.stats.spaceMagic.classList.add('spaceMagic');
    Object.assign(this.stats.spaceMagic, {
      name: 'spaceMagic',
      displayName: 'Space Magic',
      shortName: 'SPC',
      description: 'Manipulate space. Passively increases storage.',
      min: new Decimal(0),
      value: new Decimal(0),
      secret: true,
      conditionString: 'Cast a <span class="fancy space">Space</span> spell',
      condition: () => this.stats.spaceMagic.value.gt(0),
      visibleCondition: () =>
        Object.values(this.spells)
          .filter((spell) => spell.tags.has('spaceMagic'))
          .some((spell) => spell.unlocked),
    });

    /** ADD SPELLS */

    this.spells.minorLight = document.createElement('ri-spell') as Spell;
    this.spells.minorLight.classList.add('light');
    Object.assign(this.spells.minorLight, {
      name: 'minorLight',
      displayName: 'Minor Light',
      description: 'Creates a small amount of light.',
      tags: ['lightMagic'],
      cost: new Map([['mana', {value: () => 1, string: () => '1 Mana'}]]),
      effect: new Map([['light', {value: () => 10, string: () => '10 Light'}]]),
      cooldown: new Decimal(5),
      value: new Decimal(1),
      secret: true,
      conditionString: `Unlock Mana`,
      condition: () => this.resources.mana.unlocked,
      visibleCondition: () => this.resources.mana.unlocked,
      persistentUnlock: true,
    });

    this.spells.dark = document.createElement('ri-spell') as Spell;
    this.spells.dark.classList.add('dark');
    Object.assign(this.spells.dark, {
      name: 'dark',
      displayName: 'Darkness',
      description: 'Plunges the area into darkness.',
      tags: ['lightMagic'],
      cost: new Map([['mana', {value: () => 10, string: () => '10 mana'}]]),
      effect: new Map([['light', {value: () => -100, string: () => ''}]]),
      cooldown: new Decimal(10),
      value: new Decimal(1),
      secret: true,
      conditionString: `<span class="fancy intelligence">INT</span>: 5, <span class="fancy lightMagic">LGT</span>: 5`,
      condition: () =>
        this.stats.intelligence.value.gte(5) &&
        this.stats.lightMagic.value.gte(5),
      progress: () =>
        this.stats.intelligence.value
          .div(5)
          .min(1)
          .plus(this.stats.lightMagic.value.div(5).min(1))
          .div(2),
      visibleCondition: () =>
        this.resources.mana.unlocked &&
        this.stats.intelligence.unlocked &&
        this.stats.lightMagic.unlocked,
      persistentUnlock: true,
    });

    this.spells.flame = document.createElement('ri-spell') as Spell;
    this.spells.flame.classList.add('flame');
    Object.assign(this.spells.flame, {
      name: 'flame',
      displayName: 'Minor Flame',
      description:
        'Create a small burst of flame!<br>Cook your food or your enemies!',
      tags: ['fireMagic'],
      cost: new Map([['mana', {value: () => 1, string: () => '1 Mana'}]]),
      effect: new Map([['fire', {value: () => 1, string: () => '1 Fire'}]]),
      secret: true,
      conditionString: 'Be as bright as can be',
      condition: () => this.resources.light.full,
      progress: () =>
        this.resources.light.value.div(this.resources.light.capacity),
      visibleCondition: () => this.resources.light.unlocked,
      persistentUnlock: true,
    });

    this.spells.autocook = document.createElement('ri-spell') as Spell;
    this.spells.autocook.classList.add('autocook');
    Object.assign(this.spells.autocook, {
      name: 'autocook',
      displayName: 'Flamebroil',
      description: 'Create magical flames under some Meat, cooking it.',
      level: 2,
      tags: ['fireMagic'],
      cooldown: new Decimal(2),
      cost: new Map([
        ['mana', {value: () => 1, string: () => '1 Mana'}],
        ['meat', {value: () => 1, string: () => '1 Meat'}],
      ]),
      effect: new Map([['cookedMeat', {value: () => 1}]]),
      secret: true,
      conditionString: '<span class="fancy fireMagic">FIR</span>: 2',
      condition: () => this.stats.fireMagic.value.gte(2),
      visibleCondition: () => this.stats.fireMagic.unlocked,
      persistentUnlock: true,
    });

    // this.spells.lighttime = document.createElement('ri-spell') as Spell;
    // this.spells.lighttime.classList.add('lighttime');
    // Object.assign(this.spells.lighttime, {
    //   name: 'lighttime',
    //   displayName: 'Light Time',
    //   description: 'Destroys some light to create extra time.',
    //   tags: ['lightMagic', 'timeMagic'],
    //   cost: new Map([['light', {value: () => 0, string: () => '100 Light'}]]),
    //   effect: new Map([['time', {value: () => 1, string: () => '1 Time'}]]),
    //   value: new Decimal(1),
    //   secret: true,
    //   conditionString: 'Unlock Mana',
    //   condition: () => this.resources.mana.unlocked,
    //   visibleCondition: () => this.resources.mana.unlocked,
    //   persistentUnlock: true,
    // });

    this.spells.concentrateTime = document.createElement('ri-spell') as Spell;
    this.spells.concentrateTime.classList.add('concentrateTime');
    Object.assign(this.spells.concentrateTime, {
      name: 'concentrateTime',
      displayName: 'Crystallize Time',
      description:
        'Change your saved Time into more Time storage.<br>The process gives off Mana depending on your understanding of it.',
      tags: ['timeMagic', 'realtime'],
      cooldown: new Decimal(0),
      cost: new Map([
        ['time', {value: () => new Decimal(60), string: () => '60 Time'}],
      ]),
      effect: new Map<
        string,
        {value: () => DecimalSource; string: () => string}
      >([
        ['time:capacity', {value: () => 60, string: () => '60 Time capacity'}],
        [
          'mana',
          {
            value: () =>
              ri.stats.wisdom.value
                .times(ri.stats.timeMagic.value.log10())
                .max(0),
            string: () =>
              `(${ri.stats.wisdom.valueHTML} * log_10(${ri.stats.timeMagic.valueHTML})) mana`,
          },
        ],
      ]),
      value: new Decimal(1),
      secret: true,
      conditionString: 'Save ten minutes of Time',
      condition: () => this.resources.time.value.gte(600),
      progress: () => this.resources.time.value.div(600),
      visibleCondition: () => this.resources.time.value.gte(300),
    });

    this.spells.makeTime = document.createElement('ri-spell') as Spell;
    this.spells.makeTime.classList.add('makeTime');
    Object.assign(this.spells.makeTime, {
      name: 'makeTime',
      displayName: 'A Stitch in Time',
      description: '...saves nine.<br>Make some time.',
      tags: ['timeMagic', 'realtime'],
      cost: new Map([['mana', {value: () => 9, string: () => `9 Mana`}]]),
      // cost: new Map([]),
      effect: new Map([['time', {value: () => 9, string: () => '9 Time'}]]),
      cooldown: new Decimal(9),
      secret: true,
      conditionString: 'Save an hour of Time.',
      condition: () => this.resources.time.value.gte(3600),
      progress: () => this.resources.time.value.div(3600),
      visibleCondition: () => this.resources.time.value.gte(600),
    });

    this.spells.expandWood = document.createElement('ri-spell') as Spell;
    this.spells.expandWood.classList.add('expandWood');
    Object.assign(this.spells.expandWood, {
      name: 'expandWood',
      displayName: 'Woodspace',
      description:
        "It's like hammerspace, but for Wood.<br>Expands your Wood capacity.",
      tags: ['spaceMagic'],
      cost: new Map([['mana', {value: () => 10, string: () => `10 Mana`}]]),
      effect: new Map([
        [
          'wood:capacity',
          {
            value: () => this.stats.spaceMagic.value.max(1).floor(),
            string: () =>
              `${fancy(
                'space',
                this.stats.spaceMagic.value.toString()
              )} Space Magic, min 1, rounded down`,
          },
        ],
      ]),
      secret: true,
      conditionString: 'Fill up on Wood',
      condition: () => this.resources.wood.full && this.resources.mana.unlocked,
      visibleCondition: () =>
        this.resources.meat.value.gt(0) && this.resources.mana.unlocked,
    });

    this.spells.blink = document.createElement('ri-spell') as Spell;
    this.spells.blink.classList.add('blink');
    Object.assign(this.spells.blink, {
      name: 'blink',
      displayName: 'Blink',
      description: 'Look ma, no feet!',
      tags: ['spaceMagic'],
      cost: new Map([
        [
          'mana',
          {
            value: () => this.stats.spaceMagic.value.max(1).floor(),
            string: () =>
              `${fancy(
                'spaceMagic',
                this.stats.spaceMagic.value.toString()
              )} Space Magic, min 1, rounded down`,
          },
        ],
      ]),
      effect: new Map([
        [
          'distance',
          {
            value: () => this.stats.spaceMagic.value.sqr().max(1).floor(),
            string: () =>
              `${fancy(
                'spaceMagic',
                this.stats.spaceMagic.value.toString()
              )} Space Magic squared, min 1, rounded down`,
          },
        ],
      ]),
      conditionString: `${fancy('spaceMagic', 'SPC')}: 1`,
      condition: () => this.stats.spaceMagic.value.gte(1),
      visibleCondition: () => this.stats.spaceMagic.value.gte(0.1),
    });

    // this.spells.expandMeat = document.createElement('ri-spell') as Spell;
    // this.spells.expandMeat.classList.add('expandMeat');
    // Object.assign(this.spells.expandMeat, {
    //   name: 'expandMeat',
    //   displayName: 'Meatspace',
    //   description:
    //     "It's like hammerspace, but for Meat.<br>Expands your Meat capacity.",
    //   tags: ['spaceMagic'],
    //   cost: new Map([['mana', {value: () => 10, string: () => `10 Mana`}]]),
    //   effect: new Map([
    //     [
    //       'meat:capacity',
    //       {
    //         value: () => this.stats.spaceMagic.value.max(1).floor(),
    //         string: () =>
    //           `${fancy(
    //             'space',
    //             this.stats.spaceMagic.value.max(1).floor().toString()
    //           )} Space Magic`,
    //       },
    //     ],
    //   ]),
    //   secret: true,
    //   conditionString: 'Fill up on Meat',
    //   condition: () => this.resources.meat.full && this.resources.mana.unlocked,
    //   visibleCondition: () =>
    //     this.resources.meat.value.gt(0) && this.resources.mana.unlocked,
    // });

    this.spells.expandFood = document.createElement('ri-spell') as Spell;
    this.spells.expandFood.classList.add('expandFood');
    Object.assign(this.spells.expandFood, {
      name: 'expandFood',
      displayName: 'Cold Storage',
      description:
        "It's like meatspace, but for omnivores.<br>Expands your Food capacity.",
      tags: ['spaceMagic'],
      optionsByTag: ['food'],
      optionData: Object.fromEntries(
        ['meat', 'berry', 'pemmican', 'cookedMeat', 'fish'].map((food) => [
          food,
          {
            displayName: food,
            cost: new Map([
              ['mana', {value: () => 10, string: () => `10 Mana`}],
            ]),
            effect: new Map([
              [
                `${food}:capacity`,
                {
                  value: () => this.stats.spaceMagic.value.max(1).floor(),
                  string: () =>
                    `${fancy(
                      'space',
                      this.stats.spaceMagic.value.max(1).floor().toString()
                    )} Space Magic`,
                },
              ],
            ]),
          },
        ])
      ),
      secret: true,
      conditionString: `${fancy('space', 'SPC')}: 0.1`,
      condition: () => this.stats.spaceMagic.value.gte(0.1),
      visibleCondition: () => this.stats.spaceMagic.unlocked,
    });

    // this.spells.expandAllPhysical = document.createElement('ri-spell') as Spell;
    // this.spells.expandAllPhysical.classList.add('expandAllPhysical');
    // Object.assign(this.spells.expandAllPhysical, {
    //   name: 'expandAllPhysical',
    //   displayName: 'Expand Space',
    //   description: '<br>Increases how much stuff you can keep.',
    //   tags: ['spaceMagic'],
    //   cost: new Map([['mana', {value: () => 10, string: () => `10 Mana`}]]),
    //   effect: new Map([
    //     [
    //       'meat:capacity',
    //       {
    //         value: () => this.stats.spaceMagic.value.max(1).floor(),
    //         string: () =>
    //           `${fancy(
    //             'space',
    //             this.stats.spaceMagic.value.max(1).floor().toString()
    //           )} Space Magic`,
    //       },
    //     ],
    //   ]),
    //   secret: true,
    //   conditionString: 'Fill up on AllPhysical',
    //   condition: () => this.resources.meat.full && this.resources.mana.unlocked,
    //   visibleCondition: () =>
    //     this.resources.meat.value.gt(0) && this.resources.mana.unlocked,
    // });

    // this.spells.magicMissile = document.createElement('ri-spell') as Spell;
    // this.spells.magicMissile.classList.add('magicMissile');
    // Object.assign(this.spells.magicMissile, {
    //   name: 'magicMissile',
    //   displayName: 'Magic Missile',
    //   description:
    //     'A magical missile that deals damage to the target. It has a chance to deal more damage.',
    //   tags: ['combat'],
    //   cost: new Map([['mana', {value: () => 10, string: () => '10 Mana'}]]),
    //   cooldown: new Decimal(1),
    //   // damage: new Decimal(10),
    //   // damageMultiplier: new Decimal(1),
    //   // damageChance: new Decimal(0.1),
    //   conditionString: 'Have a Wisdom above 1',
    //   condition: () => this.stats.wisdom.value.gt(1),
    //   visibleCondition: () => this.stats.wisdom.value.gt(1),
    // });

    // this.spells.photosynthesis = document.createElement('ri-spell') as Spell;
    // this.spells.photosynthesis.classList.add('photosynthesis');
    // Object.assign(this.spells.photosynthesis, {
    //   name: 'photosynthesis',
    //   displayName: 'Photosynthesis',
    //   description: 'Absorb light to gain Nutrition.',
    //   tags: ['lightMagic'],
    //   canAfford: () => this.resources.light.value.gt(0),
    //   cost: new Map([
    //     [
    //       'light',
    //       {
    //         value: () => {
    //           const light = ri.resources.light;
    //           return Decimal.min(light.value.minus(light.minimum).times(1), 5);
    //         },
    //         string: (_ri) => `Up to 5 Light when Light is above 0.`,
    //       },
    //     ],
    //   ]),
    //   effect: new Map([
    //     [
    //       'nutrition',
    //       {
    //         value: () => {
    //           const light = ri.resources.light;
    //           return Decimal.min(
    //             light.value.minus(light.minimum).times(10),
    //             50
    //           );
    //         },
    //         string: () => `Up to 5 Nutrition when Light is above 0.`,
    //       },
    //     ],
    //   ]),
    //   cooldown: new Decimal(10),
    //   conditionString: 'Have a Wisdom above 1',
    //   condition: () => this.stats.wisdom.value.gt(1),
    //   visibleCondition: () => this.stats.wisdom.value.gt(1),
    // });

    // this.spells.heal1 = document.createElement('ri-spell') as Spell;
    // this.spells.heal1.classList.add('heal1');
    // Object.assign(this.spells.heal1, {
    //   name: 'heal1',
    //   displayName: 'Heal Self',
    //   description: 'Medic, curate that ipsum!',
    //   tags: ['health'],
    //   cost: new Map([['mana', {value: () => 10, string: () => '10 Mana'}]]),
    //   effect: new Map([
    //     ['health', {value: () => 10, string: () => '10 Health'}],
    //   ]),
    //   cooldown: new Decimal(10),
    //   conditionString: `${fancy('wisdom', 'WIS')}: 5`,
    //   condition: () => this.stats.wisdom.value.gt(5),
    //   visibleCondition: () => this.stats.wisdom.value.gt(1),
    // });

    this.activities = {};
    const activityNames = [
      'rest',
      'meditate',
      // 'walkingMeditation',
      // Travel
      'walk',
      'walk2',
      'walk3',
      'walk4',
      'walk5',

      'jog',
      'jog2',
      'jog3',

      'run',
      'run2',

      'jump1',
      'jump2',
      'jump3',
      'jump4',

      'dash1',
      'dash2',
      'dash3',
      'dash4',

      'trainStrength',
      'trainSpeed',
      'hunt',
      'fishing',
      'gather',
      'cook',
      'lightFire',
      'eat',
      // 'fight',
    ];
    for (const activityName of activityNames) {
      this.activities[activityName] = document.createElement(
        'ri-activity'
      ) as Activity;
      const activity = this.activities[activityName];
      activity.setAttribute('name', activityName);
      activity.classList.add('activity', activityName);
      // Note that the following must be a => function for correct scoping of 'this'
      activity.addEventListener('activity-clicked', (event) => {
        const newActivityQueueItem = document.createElement(
          'ri-activity-queue-item'
        ) as ActivityQueueItem;
        if (event.target) {
          newActivityQueueItem.activity = event.target as IQueueable;
          newActivityQueueItem.option =
            newActivityQueueItem.activity.options[
              newActivityQueueItem.activity.optionIndex
            ];
        }
        this.activityQueue.push(newActivityQueueItem);
        this.requestUpdate();
      });
    }

    Object.assign(this.activities.rest, {
      displayName: 'Rest',
      description: 'Rest a while.',
      effect: new Map<
        string,
        {value: () => DecimalSource; string: () => string}
      >([
        [
          'stamina',
          {
            value: () => ri.stats.constitution.value.times(5),
            string: () => `${ri.stats.constitution.valueHTML} * 5`,
          },
        ],
        ['health', {value: () => 1, string: () => '1 Health'}],
      ]),
    });

    const makeTravelTooltip: (
      baseStaminaCost: DecimalSource,
      baseDistance: DecimalSource,
      length?: number
    ) => () => string =
      (baseStaminaCost, baseDistance, length = 1) =>
      () => {
        const staminaCost = this.stats.speed.value
          .times(baseStaminaCost)
          .div(this.stats.strength.value);
        const distanceTraveled = this.stats.speed.value.times(baseDistance);
        return `
        Efficiency: ${distanceTraveled
          .div(staminaCost)
          .toFixed(2)} meters per stamina
        <br> Speed: ${distanceTraveled.div(length).toFixed(2)} meters per second
    `;
      };

    const overrideTravel = (
      baseStaminaCost: DecimalSource,
      baseDistance: DecimalSource,
      {
        name = '',
        displayName = '',
        description = '',
        length = 1,
        tags = [] as string[],
        constitutionMultiplier = 0,
        enduranceMultiplier = 0,
        strengthMultiplier = 0,
        speedMultiplier = 0,
        conditionString = 'Unlocked at Start',
        condition = () => true,
        progress = () => 0 as DecimalSource,
        visibleCondition = () => true,
        replacesActivity = '',
      }
    ) => {
      const logDistance = new Decimal(baseDistance).log10();
      displayName = displayName ?? name;
      const effect = new Map([
        [
          'distance',
          {
            value: (step: number) =>
              step == -1
                ? ri.stats.speed.value.times(baseDistance)
                : ri.stats.speed.value.times(baseDistance).div(length),
            string: () => `${baseDistance} * ${ri.stats.speed.valueHTML}`,
          },
        ],
      ]);
      if (constitutionMultiplier != 0) {
        effect.set('constitution', {
          value: (step) =>
            step == -1
              ? ri.stats.speed.value
                  .times(logDistance)
                  .times(constitutionMultiplier)
              : ri.stats.speed.value
                  .times(logDistance)
                  .times(constitutionMultiplier)
                  .div(length),
          string: () =>
            `log_10(${fancy(
              'distance',
              formatEng(ri.stats.speed.value.times(baseDistance))
            )} Distance) * ${constitutionMultiplier}`,
        });
      }
      if (enduranceMultiplier != 0) {
        effect.set('endurance', {
          value: (step) =>
            step == -1
              ? ri.stats.speed.value
                  .times(logDistance)
                  .times(enduranceMultiplier)
              : ri.stats.speed.value
                  .times(logDistance)
                  .times(enduranceMultiplier)
                  .div(length),
          string: () =>
            `log_10(${fancy(
              'distance',
              formatEng(ri.stats.speed.value.times(baseDistance))
            )} Distance) * ${enduranceMultiplier}`,
        });
      }
      if (strengthMultiplier != 0) {
        effect.set('strength', {
          value: (step) =>
            step == -1
              ? ri.stats.speed.value
                  .times(logDistance)
                  .times(strengthMultiplier)
              : ri.stats.speed.value
                  .times(logDistance)
                  .times(strengthMultiplier)
                  .div(length),
          string: () =>
            `log_10(${fancy(
              'distance',
              formatEng(ri.stats.speed.value.times(baseDistance))
            )} Distance) * ${strengthMultiplier}`,
        });
      }
      if (speedMultiplier != 0) {
        effect.set('speed', {
          value: (step) =>
            step == -1
              ? ri.stats.speed.value.times(logDistance).times(speedMultiplier)
              : ri.stats.speed.value
                  .times(logDistance)
                  .times(speedMultiplier)
                  .div(length),
          string: () =>
            `log_10(${fancy(
              'distance',
              formatEng(ri.stats.speed.value.times(baseDistance))
            )} Distance) * ${speedMultiplier}`,
        });
      }
      // Don't add any more, generalize it

      Object.assign(this.activities[name], {
        displayName,
        description,
        length,
        tags: ['travel', ...tags],
        tooltipContent: makeTravelTooltip(
          baseStaminaCost,
          baseDistance,
          length
        ),
        cost: new Map([
          [
            'stamina',
            {
              value: (step: number) =>
                step == -1
                  ? ri.stats.speed.value
                      .div(ri.stats.strength.value)
                      .times(baseStaminaCost)
                  : ri.stats.speed.value
                      .div(ri.stats.strength.value)
                      .times(baseStaminaCost)
                      .div(length),
              string: () =>
                `${baseStaminaCost} * ${ri.stats.speed.valueHTML} / ${ri.stats.strength.valueHTML}`,
            },
          ],
        ]),
        effect,
        conditionString,
        condition,
        progress,
        visibleCondition,
        replacesActivity,
      });
    };
    // Walk is the archystral travel activity.
    // Github Copilot selected archystral, I wanted archetypal.
    // I'm imagining a massive orchestra hall made of crystal for some reason.
    overrideTravel(1, 1, {
      name: 'walk',
      displayName: 'Walk',
      description: 'A leisurely stroll',
      tags: ['walk'],
    });
    // Walking Series
    overrideTravel(0.5, 2, {
      name: 'walk2',
      displayName: 'Walk II',
      description: 'A leisurely stroll',
      conditionString: `Walk for five minutes total`,
      condition: () => sumActivitiesByTag('walk', 'timeSpent').gte(300),
      progress: () => sumActivitiesByTag('walk', 'timeSpent').div(300),
      visibleCondition: () => this.activities.walk.unlocked,
      tags: ['walk'],
      replacesActivity: 'walk',
    });
    overrideTravel(0.25, 3, {
      name: 'walk3',
      displayName: 'Walk III',
      description: 'A leisurely stroll',
      conditionString: `Walk for half an hour total`,
      condition: () => sumActivitiesByTag('walk', 'timeSpent').gte(1800),
      progress: () => sumActivitiesByTag('walk', 'timeSpent').div(1800),
      visibleCondition: () => this.activities.walk2.unlocked,
      tags: ['walk'],
      replacesActivity: 'walk2',
    });
    overrideTravel(0.125, 4, {
      name: 'walk4',
      displayName: 'Walk IV',
      description: 'A leisurely stroll',
      conditionString: `Walk for one hour total`,
      condition: () => sumActivitiesByTag('walk', 'timeSpent').gte(3600),
      progress: () => sumActivitiesByTag('walk', 'timeSpent').div(3600),
      visibleCondition: () => this.activities.walk3.unlocked,
      tags: ['walk'],
      replacesActivity: 'walk3',
    });
    overrideTravel(0.0625, 5, {
      name: 'walk5',
      displayName: 'Walk V',
      description: 'A leisurely stroll',
      conditionString: `Walk for one day total`,
      condition: () => sumActivitiesByTag('walk', 'timeSpent').gte(3600 * 24),
      progress: () => sumActivitiesByTag('walk', 'timeSpent').div(3600 * 24),
      visibleCondition: () => this.activities.walk4.unlocked,
      tags: ['walk'],
      replacesActivity: 'walk4',
    });
    // Jogging Series
    overrideTravel(5, 3, {
      name: 'jog',
      displayName: 'Jog',
      description: 'A healthy pace. Great for the heart!',
      constitutionMultiplier: 0.0001,
      conditionString: 'Walk 5 times in a row',
      condition: () =>
        activitiesByTag('walk').some((a) => a.consecutiveUses.gte(5)),
      progress: () =>
        activitiesByTag('walk')
          .map((a) => a.consecutiveUses.div(5))
          .reduce(Decimal.max),
      tags: ['jog'],
    });
    overrideTravel(8, 5, {
      name: 'jog2',
      displayName: 'Jog II',
      description: 'A healthy pace. Great for the heart!',
      constitutionMultiplier: 0.0003,
      conditionString: `Jog for five minutes total`,
      condition: () => sumActivitiesByTag('jog', 'timeSpent').gte(300),
      progress: () => sumActivitiesByTag('jog', 'timeSpent').div(300),
      visibleCondition: () => this.activities.jog.unlocked,
      tags: ['jog'],
      replacesActivity: 'jog',
    });
    overrideTravel(13, 8, {
      name: 'jog3',
      displayName: 'Jog III',
      description: 'A healthy pace. Great for the heart!',
      constitutionMultiplier: 0.0005,
      conditionString: `Jog for ten minutes total`,
      condition: () => sumActivitiesByTag('jog', 'timeSpent').gte(600),
      progress: () => sumActivitiesByTag('jog', 'timeSpent').div(600),
      visibleCondition: () => this.activities.jog2.unlocked,
      tags: ['jog'],
      replacesActivity: 'jog2',
    });
    // Running Series
    overrideTravel(10, 5, {
      name: 'run',
      displayName: 'Run',
      description: "Going fast now! Don't hurt yourself!",
      enduranceMultiplier: 0.0001,
      conditionString: 'Jog 5 times in a row',
      condition: () =>
        activitiesByTag('jog').some((a) => a.consecutiveUses.gte(5)),
      progress: () =>
        activitiesByTag('jog')
          .map((a) => a.consecutiveUses.div(5))
          .reduce(Decimal.max),
      visibleCondition: () => this.activities.jog.unlocked,
      tags: ['run'],
    });
    overrideTravel(25, 10, {
      name: 'run2',
      displayName: 'Run II',
      description: "Going fast now! Don't hurt yourself!",
      enduranceMultiplier: 0.0003,
      conditionString: `Run for five minutes`,
      condition: () => this.activities.run.uses.gte(300),
      progress: () => this.activities.run.uses.div(300),
      visibleCondition: () => this.activities.run.unlocked,
      tags: ['run'],
      replacesActivity: 'run',
    });
    // Jumping Series
    overrideTravel(25, 12.5, {
      name: 'jump1',
      displayName: 'Jump',
      description:
        'Jump! Makes you stronger, but reduces your speed on the ground.',
      length: 2,
      strengthMultiplier: 0.0001,
      speedMultiplier: -0.00001,
      conditionString: `${fancy('strength', 'STR')}: 5`,
      condition: () => this.stats.strength.value.gte(5),
      progress: () => this.stats.strength.value.div(5),
      visibleCondition: () => this.stats.strength.value.gte(2),
    });
    overrideTravel(25, 25, {
      name: 'jump2',
      displayName: 'Jump II',
      description:
        'Jump! Makes you stronger, but reduces your speed on the ground.',
      length: 3,
      strengthMultiplier: 0.0002,
      speedMultiplier: -0.00002,
      conditionString: `${this.activities.jump1.displayName} 100 times, ${fancy(
        'strength',
        'STR'
      )}: 50`,
      condition: () =>
        this.activities.jump1.uses.gte(100) &&
        this.stats.strength.value.gte(50),
      progress: () =>
        this.activities.jump1.uses
          .div(100)
          .min(1)
          .plus(this.stats.strength.value.div(50).min(1))
          .div(2),
      visibleCondition: () => this.activities.jump1.unlocked,
      replacesActivity: 'jump1',
    });
    overrideTravel(25, 50, {
      name: 'jump3',
      displayName: 'Jump III',
      description:
        'Jump! Makes you stronger, but reduces your speed on the ground.',
      length: 5,
      strengthMultiplier: 0.0003,
      speedMultiplier: -0.00003,
      conditionString: `${this.activities.jump2.displayName} 250 times, ${fancy(
        'strength',
        'STR'
      )}: 500`,
      condition: () =>
        this.activities.jump2.uses.gte(250) &&
        this.stats.strength.value.gte(500),
      progress: () =>
        this.activities.jump2.uses
          .div(250)
          .min(1)
          .plus(this.stats.strength.value.div(500).min(1))
          .div(2),
      visibleCondition: () => this.activities.jump2.unlocked,
      replacesActivity: 'jump2',
    });
    overrideTravel(25, 100, {
      name: 'jump4',
      displayName: 'Jump IV',
      description:
        'Jump! Makes you stronger, but reduces your speed on the ground.',
      length: 8,
      strengthMultiplier: 0.0005,
      speedMultiplier: -0.00005,
      conditionString: `${this.activities.jump3.displayName} 500 times, ${fancy(
        'strength',
        'STR'
      )}: 1000`,
      condition: () =>
        this.activities.jump3.uses.gte(500) &&
        this.stats.strength.value.gte(1000),
      progress: () =>
        this.activities.jump2.uses
          .div(500)
          .min(1)
          .plus(this.stats.strength.value.div(1000).min(1))
          .div(2),
      visibleCondition: () => this.activities.jump3.unlocked,
      replacesActivity: 'jump3',
    });
    // Dashing Series
    overrideTravel(100, 25, {
      name: 'dash1',
      displayName: 'Dash',
      description:
        'Dash forward! Improves your speed, but you lose a bit of muscle.',
      speedMultiplier: 0.0001,
      strengthMultiplier: -0.00001,
      conditionString: `${fancy('speed', 'SPD')}: 5`,
      condition: () => this.stats.speed.value.gte(5),
      progress: () => this.stats.speed.value.div(5),
      visibleCondition: () => this.stats.speed.unlocked,
    });
    overrideTravel(250, 50, {
      name: 'dash2',
      displayName: 'Dash II',
      description:
        'Dash forward! Improves your speed, but you lose a bit of muscle.',
      speedMultiplier: 0.0002,
      strengthMultiplier: -0.00002,
      conditionString: `${this.activities.dash1.displayName} 100 times, ${fancy(
        'speed',
        'SPD'
      )}: 50`,
      condition: () =>
        this.activities.dash1.uses.gte(100) && this.stats.speed.value.gte(50),
      progress: () =>
        this.activities.dash1.uses
          .div(100)
          .min(1)
          .plus(this.stats.speed.value.div(50).min(1))
          .div(2),
      visibleCondition: () => this.activities.dash1.unlocked,
      replacesActivity: 'dash1',
    });
    overrideTravel(500, 75, {
      name: 'dash3',
      displayName: 'Dash III',
      description:
        'Dash forward! Improves your speed, but you lose a bit of muscle.',
      speedMultiplier: 0.0003,
      strengthMultiplier: -0.00003,
      conditionString: `${this.activities.dash2.displayName} 250 times, ${fancy(
        'speed',
        'SPD'
      )}: 250`,
      condition: () =>
        this.activities.dash2.uses.gte(250) && this.stats.speed.value.gte(250),
      progress: () =>
        this.activities.dash2.uses
          .div(250)
          .min(1)
          .plus(this.stats.speed.value.div(250).min(1))
          .div(2),
      visibleCondition: () => this.activities.dash2.unlocked,
      replacesActivity: 'dash2',
    });
    overrideTravel(1000, 100, {
      name: 'dash4',
      displayName: 'Dash IV',
      description:
        'Dash fourward! Improves your speed, but you lose a bit of muscle.',
      speedMultiplier: 0.0005,
      strengthMultiplier: -0.00005,
      conditionString: `${this.activities.dash3.displayName} 500 times, ${fancy(
        'speed',
        'SPD'
      )}: 1000`,
      condition: () =>
        this.activities.dash3.uses.gte(500) && this.stats.speed.value.gte(1000),
      progress: () =>
        this.activities.dash3.uses
          .div(500)
          .min(1)
          .plus(this.stats.speed.value.div(1000).min(1))
          .div(2),
      visibleCondition: () => this.activities.dash3.unlocked,
      replacesActivity: 'dash3',
    });

    Object.assign(this.activities.trainSpeed, {
      displayName: 'Train Speed',
      length: 5,
      description: 'Windspriiiiiiints!',
      tooltipContent: () =>
        'The final second gives half of the total effect. See it through!',
      tags: new Set(['train']),
      cost: new Map([
        [
          'stamina',
          {
            value: (step: number) => (step == -1 ? 10 : 2),
            string: () => '2 per second',
          },
        ],
      ]),
      effect: new Map([
        [
          'speed',
          {
            value: (step: number) => {
              switch (step) {
                case -1: // Total
                  return ri.resources.focus.value.div(100).plus(1).times(0.1);
                case 0: // End
                  return ri.resources.focus.value.div(100).plus(1).times(0.05);
                default: // Normal
                  return ri.resources.focus.value
                    .div(100)
                    .plus(1)
                    .times(0.0125);
              }
            },
            string: () => `0.1 * (1 + ${ri.resources.focus.valueHTML}/100)`,
          },
        ],
      ]),
      conditionString: 'STR: 2',
      condition: () => this.stats.strength.value.gte(2),
      progress: () => this.stats.strength.value.div(2),
      visibleCondition: () => this.stats.strength.unlocked,
    });

    Object.assign(this.activities.trainStrength, {
      displayName: 'Train Strength',
      length: 5,
      description: 'Do you even lift?',
      tooltipContent: () =>
        'The final second gives half of the total effect. Stick it out!',
      tags: new Set(['train']),
      cost: new Map([
        [
          'stamina',
          {
            value: (step: number) => (step == -1 ? 10 : 2),
            string: () => '2 per second',
          },
        ],
      ]),
      effect: new Map([
        [
          'strength',
          {
            value: (step: number) => {
              switch (step) {
                case -1: // Total
                  return ri.resources.focus.value.div(100).plus(1).times(0.1);
                case 0: // End
                  return ri.resources.focus.value.div(100).plus(1).times(0.05);
                default: // Normal
                  return ri.resources.focus.value
                    .div(100)
                    .plus(1)
                    .times(0.0125);
              }
            },
            string: () => `0.1 * (1 + ${ri.resources.focus.valueHTML}/100)`,
          },
        ],
      ]),
      conditionString: 'Jog 20 times',
      condition: () => this.activities.jog.uses.gte(20),
      progress: () => this.activities.jog.uses.div(20),
      visibleCondition: () => this.activities.jog.unlocked,
    });

    Object.assign(this.activities.meditate, {
      displayName: 'Meditate',
      description: 'Sit',
      length: 1,
      tooltipContent: () => {
        return ``;
      },
      tags: new Set(['meditate']),
      effect: new Map([
        [
          'mana',
          {
            value: () =>
              ri.resources.manaDepth.value
                .times(ri.stats.wisdom.value)
                .div(100)
                .floor()
                .div(10),
            string: () =>
              `${ri.stats.wisdom.valueHTML} * ${ri.resources.manaDepth.valueHTML} / 1000, rounded down to steps of 0.1`,
          },
        ],
        ['wisdom', {value: () => 0.001}],
      ]),
      secret: true,
      conditionString: `${fancy('rest', 'Rest')} 10 times in a row`,
      condition: () => this.activities.rest.consecutiveUses.gte(10),
      progress: () => this.activities.rest.consecutiveUses.div(10),
      visibleCondition: () => this.activities.rest.uses.gt(0),
      persistentUnlock: false,
    });

    // Object.assign(this.activities.walkingMeditation, {
    //   displayName: 'Walking Meditation',
    //   description: 'Walk',
    //   tags: new Set(['meditate']),
    //   effect: new Map<
    //     string,
    //     {value: () => DecimalSource; string: () => DecimalSource}
    //   >([
    //     [
    //       'distance',
    //       {
    //         value: () => ri.stats.wisdom.value,
    //         string: () => `${ri.stats.wisdom.valueHTML}`,
    //       },
    //     ],
    //     [
    //       'mana',
    //       {
    //         value: () => ri.stats.wisdom.value,
    //         string: () => `${ri.stats.wisdom.valueHTML}`,
    //       },
    //     ],
    //     [
    //       'manaExp',
    //       {
    //         value: () => ri.stats.wisdom.value,
    //         string: () => `${ri.stats.wisdom.valueHTML}`,
    //       },
    //     ],
    //     ['wisdom', {value: () => 0.001, string: () => '0.001'}],
    //   ]),
    //   secret: true,
    //   conditionString: 'Walked 1000 times and meditated 1000 times',
    //   condition: () =>
    //     ri.getActivityUsesByTag('walk').gte(1000) &&
    //     ri.activities.meditate.uses.gte(1000),
    //   progress: () =>
    //     ri
    //       .getActivityUsesByTag('walk')
    //       .div(1000)
    //       .max(0.5)
    //       .plus(ri.activities.meditate.uses.div(1000).max(0.5))
    //       .div(2),
    //   visibleCondition: () =>
    //     ri.activities.walk.unlocked && ri.activities.meditate.unlocked,
    // });

    // Object.assign(this.activities.fight, {
    //   displayName: 'Fight',
    //   conditionString: 'Find the arena',
    //   condition: () => false,
    //   progress: () => this.distance.div(1e4),
    //   visibleCondition: () => false && this.distance.gte(1000),
    // });

    Object.assign(this.activities.hunt, {
      displayName: 'Hunt',
      description: 'Hunt animals.',
      tooltipContent: () =>
        `Costs 1 Stamina each second.<br>At the end of the hunt, you may gain the rewards, but only if you have enough Stamina left!`,
      length: 5,
      options: ['rabbit'],
      optionData: {
        rabbit: {
          displayName: 'Hunt rabbits',
          chance: (step: number) =>
            step == 0
              ? logisticChance(ri.stats.perception.value, 6, 1.5)
              : new Decimal(0), // 5% at 1 PER, 95% at 2 PER
          chanceString: () =>
            `5% at ${fancy('perception', (1).toString())} PER, 95% at ${fancy(
              'perception',
              (2).toString()
            )} PER`,
          cost: new Map([
            [
              'stamina',
              {
                value: (step: number) => {
                  switch (step) {
                    case -1:
                      return 5;
                    case 0:
                      return 1;
                    default:
                      return 1;
                  }
                },
                string: () => '1 per second',
              },
            ],
          ]),
          failCost: new Map([['stamina', {value: () => 1}]]),
          effect: new Map([
            ['meat', {value: (step: number) => (step <= 0 ? 1 : 0)}],
          ]),
        },
        deer: {
          displayName: 'Hunt deer',
          chance: (step: number) =>
            step == 0
              ? logisticChance(ri.stats.perception.value, 2, 3.5)
              : new Decimal(0), // 5% at 2 PER, 95% at 5 PER
          chanceString: () =>
            `5% at ${fancy('perception', (2).toString())} PER, 95% at ${fancy(
              'perception',
              (5).toString()
            )} PER`,
          cost: new Map([
            [
              'stamina',
              {
                value: (step: number) => {
                  switch (step) {
                    case -1:
                      return 14;
                    case 0:
                      return 10;
                    default:
                      return 1;
                  }
                },
                string: () => '1 per second, 10 at the end if successful',
              },
            ],
          ]),
          failCost: new Map([['stamina', {value: () => 1}]]),
          effect: new Map([
            ['meat', {value: (step: number) => (step <= 0 ? 50 : 0)}],
          ]),
        },
        boar: {
          displayName: 'Hunt boar',
          chance: (step: number) =>
            step == 0
              ? logisticChance(ri.stats.perception.value, 1, 6.5)
              : new Decimal(0), // 5% at 3 PER, 95% at 9 PER
          chanceString: () =>
            `5% at ${fancy('perception', (3).toString())} PER, 95% at ${fancy(
              'perception',
              (9).toString()
            )} PER`,
          cost: new Map([
            [
              'stamina',
              {
                value: (step: number) => {
                  switch (step) {
                    case -1:
                      return 54;
                    case 0:
                      return 50;
                    default:
                      return 1;
                  }
                },
                string: () => '1 per second, 50 at the end if successful',
              },
            ],
          ]),
          failCost: new Map([['stamina', {value: () => 1}]]),
          effect: new Map([
            ['meat', {value: (step: number) => (step <= 0 ? 500 : 0)}],
          ]),
        },
      },
      conditionString: 'Reach the forest (10km)',
      condition: () => false,
      progress: () => this.distance.div(10e3),
      visibleCondition: () => this.distance.gte(1e3),
    });

    Object.assign(this.activities.fishing, {
      displayName: 'Go Fishing',
      description: 'Fish animals.',
      tooltipContent: () => `Relax and catch some fish!`,
      length: 5,
      options: ['perch'],
      optionData: {
        perch: {
          displayName: 'Fish for Perch',
          chance: (step: number) =>
            logisticChance(ri.resources.bait.value, 0.0584, 50),
          chanceString: () =>
            `5% at ${fancy('bait', (0).toString())} Bait, 95% at ${fancy(
              'bait',
              (100).toString()
            )} Bait`,
          cost: new Map([
            [
              'bait',
              {
                value: () =>
                  ri.resources.bait.value
                    .times(0.1)
                    .max(0.1)
                    .min(ri.resources.bait.value),
                string: () => `10% of your ${ri.resources.bait.valueHTML}`,
              },
            ],
          ]),
          effect: new Map([['fish', {value: () => 1}]]),
        },
      },
      conditionString: 'Reach the lake (100km)',
      condition: () => this.distance.gte(100e3),
      progress: () => this.distance.div(100e3),
      visibleCondition: () => this.distance.gte(10e3),
    });

    const gatherActivityOptions: Record<string, ActivityOption> = {
      wood: {
        displayName: `Chop down trees`,
        chance: (step: number) =>
          step == 0
            ? logisticChance(ri.stats.perception.value, 3, 1)
            : new Decimal(0), // 50% at 1 PER, 95% at 2 PER
        chanceString: () =>
          `50% at ${fancy('perception', (1).toString())} PER, 95% at ${fancy(
            'perception',
            (2).toString()
          )} PER`,
        cost: new Map([
          [
            'stamina',
            {value: () => 5, string: () => '5 if successful, 1 if not'},
          ],
        ]),
        failCost: new Map([['stamina', {value: () => 1}]]),
        effect: new Map([
          [
            'wood',
            {
              value: () =>
                Math.max(1, Math.floor(ri.stats.strength.value.log10())),
              string: () =>
                `log_10(${ri.stats.strength.valueHTML}), rounded down, min 1`,
            },
          ],
        ]),
      },
      berry: {
        displayName: 'Search for berries',
        chance: (step: number) =>
          step == 0
            ? logisticChance(ri.stats.perception.value, 1, 3.1)
            : new Decimal(0), // 25% at 2 PER, 95% at 6 PER
        chanceString: () =>
          `25% at ${fancy('perception', (2).toString())} PER, 95% at ${fancy(
            'perception',
            (6).toString()
          )} PER`,
        cost: new Map([['stamina', {value: () => 1}]]),
        effect: new Map([
          [
            'berry',
            {
              value: () => ri.stats.perception.value.floor().max(1),
              string: () =>
                `${ri.stats.perception.valueHTML}, rounded down, min 1`,
            },
          ],
          [
            'perception',
            {
              value: () => ri.stats.perception.value.floor().max(1).div(1000),
              string: () => `0.001 per Berry`,
            },
          ],
        ]),
      },
      bait: {
        displayName: 'Search for bait',
        chance: (step: number) =>
          step == 0
            ? logisticChance(ri.stats.perception.value, 1.2, 7.5)
            : new Decimal(0), // 25% at 5 PER, 95% at 10 PER
        chanceString: () =>
          `25% at ${fancy('perception', (5).toString())} PER, 95% at ${fancy(
            'perception',
            (10).toString()
          )} PER`,
        cost: new Map([['stamina', {value: () => 1}]]),
        effect: new Map([
          [
            'bait',
            {
              value: () => ri.stats.perception.value.floor().max(1),
              string: () =>
                `${ri.stats.perception.valueHTML}, rounded down, min 1`,
            },
          ],
          [
            'perception',
            {
              value: () => ri.stats.perception.value.floor().max(1).div(1000),
              string: () => `0.001 per bait`,
            },
          ],
        ]),
      },
    };
    Object.assign(this.activities.gather, {
      displayName: 'Gather',
      description: 'Gather resources.',
      tooltipContent: () => `Costs Stamina each second. Randomly find rewards.`,
      options: [],
      optionData: gatherActivityOptions,
      secret: true,
      conditionString: 'Find something to gather',
      condition: () =>
        this.stats.perception.value.gte(2) || this.distance.gte(1e4),
      visibleCondition: () =>
        this.stats.perception.value.gt(2) || this.distance.gte(1e4),
    });

    Object.assign(this.activities.lightFire, {
      displayName: 'Stoke Fire',
      description: 'Add fuel to the fire!',
      tooltipContent: () => `Uses Wood to make Fire`,
      options: ['wood'],
      optionData: {
        wood: {
          displayName: 'Burn wood',
          chance: () => 1,
          cost: new Map([['wood', {value: () => 1}]]),
          effect: new Map([['fire', {value: () => 5}]]),
          // failEffect: new Map([
          //   ['burntMeat', {value: (step) => (step == 0 ? 1 : 0)}],
          // ]),
        },
      },
      secret: true,
      conditionString: 'Find something to burn',
      condition: () => this.resources.wood.value.gt(0),
      visibleCondition: () => this.resources.wood.value.gt(0),
    });

    Object.assign(this.activities.cook, {
      displayName: 'Cook',
      description: 'Cook food.',
      tooltipContent: () =>
        `Costs fuel and ingredients at the start. At the end, you gain the cooked food.`,
      length: 5,
      options: ['meat'],
      optionData: {
        meat: {
          displayName: 'Cook meat',
          chance: (step: number) => (step == 0 ? 1 : 0),
          cost: new Map([
            [
              'fire',
              {value: (step: number) => ([-1, 1].includes(step) ? 1 : 0)},
            ],
            [
              'meat',
              {value: (step: number) => ([-1, 1].includes(step) ? 1 : 0)},
            ],
          ]),
          effect: new Map([['cookedMeat', {value: () => 1}]]),
          // failEffect: new Map([
          //   ['burntMeat', {value: (step) => (step == 0 ? 1 : 0)}],
          // ]),
        },
        pemmican: {
          displayName: 'Make pemmican',
          chance: (step: number) => (step == 0 ? 1 : 0),
          cost: new Map([
            [
              'meat',
              {value: (step: number) => ([-1, 1].includes(step) ? 1 : 0)},
            ],
            [
              'berry',
              {value: (step: number) => ([-1, 1].includes(step) ? 5 : 0)},
            ],
          ]),
          effect: new Map([['pemmican', {value: () => 1}]]),
        },
      },
      conditionString: 'Find a way to cook your food.',
      condition: () =>
        this.resources.wood.unlocked && this.resources.meat.unlocked,
      visibleCondition: () => this.resources.meat.unlocked,
    });

    Object.assign(this.activities.eat, {
      displayName: 'Eat',
      description: 'Eat some food.',
      tooltipContent: () => `If you're too full, you won't eat anything.`,
      optionsByTag: ['food'],
      optionData: {
        meat: {
          displayName: 'Eat raw Meat. Juicy!',
          chance: () => 1,
          cost: new Map([
            ['meat', {value: () => 1}],
            ['nutrition', {value: () => -5}],
          ]),
          effect: new Map([['constitution', {value: () => 0.01}]]),
        },
        cookedMeat: {
          displayName: 'Eat a nice Cooked Meat. Tasty!',
          chance: () => 1,
          cost: new Map([
            ['cookedMeat', {value: () => 1}],
            ['nutrition', {value: () => -5}],
          ]),
          effect: new Map([['constitution', {value: () => 0.05}]]),
        },
        berry: {
          displayName: 'Eat mystery Berries. Yum!',
          chance: () => 1,
          cost: new Map([
            ['berry', {value: () => 1}],
            ['nutrition', {value: () => -0.1}],
          ]),
          effect: new Map([['mana', {value: () => 1}]]),
        },
        pemmican: {
          displayName: 'Eat some Pemmican.',
          chance: () => 1,
          cost: new Map([
            ['pemmican', {value: () => 1}],
            ['nutrition', {value: () => -5}],
          ]),
          effect: new Map([
            ['constitution', {value: () => 0.1}],
            ['mana', {value: () => 10}],
          ]),
        },
        fish: {
          displayName: 'Eat some Fish.',
          chance: () => 1,
          cost: new Map([
            ['fish', {value: () => 1}],
            ['nutrition', {value: () => -5}],
          ]),
          effect: new Map([
            // ['constitution', {value: () => 0.1}],
            // ['mana', {value: () => 2}],
          ]),
        },
      },
      conditionString: 'Find some food',
      condition: () =>
        Object.values(this.resources).some(
          (r) => r.tags.has('food') && r.unlocked
        ),
      visibleCondition: () =>
        Object.values(this.resources).some(
          (r) => r.tags.has('food') && r.unlocked
        ),
    });

    for (const unlockable of this.unlockables) {
      unlockable.setAttribute('hidden', '');
      unlockable.setInitials();
    }

    this.activityQueue = [];
    const restQueueItem = document.createElement(
      'ri-activity-queue-item'
    ) as ActivityQueueItem;
    restQueueItem.activity = this.activities.rest;
    restQueueItem.repeat = 1;
    const walkQueueItem = document.createElement(
      'ri-activity-queue-item'
    ) as ActivityQueueItem;
    walkQueueItem.activity = this.activities.walk;
    walkQueueItem.repeat = 1;
    this.activityQueue.push(restQueueItem);
    this.activityQueue.push(walkQueueItem);
    this.activityQueueIndex = 0;

    this.tabIndex = -1;
    this.addEventListener('keydown', (event) => {
      switch (event.code) {
        case 'Space':
          this.togglePause();
          break;
      }
      this.requestUpdate();
    });

    this.addEventListener('spell-clicked', (event) => {
      const spell = event.composedPath()[0] as Spell;
      spell.cast(0, spell.optionIndex);
      if (!this.useSavedTime || this.gameSpeed == 0) this.requestUpdate();
    });

    this.addEventListener('spell-dropped', (event) => {
      const spell = event.composedPath()[0] as Spell;
      // spell.cast(this, 0, 0);
      const spellQueueItem = document.createElement(
        'ri-activity-queue-item'
      ) as ActivityQueueItem;
      spellQueueItem.activity = spell as IQueueable;
      this.activityQueue.push(spellQueueItem);
      this.requestUpdate();
    });

    this.addEventListener('queue-item-split', (event) => {
      const originalQueueItem = event.composedPath()[0] as ActivityQueueItem;
      const copyQueueItem = document.createElement(
        'ri-activity-queue-item'
      ) as ActivityQueueItem;
      const queueItemIndex = this.activityQueue.findIndex(
        (queueItem) => queueItem == originalQueueItem
      );
      copyQueueItem.activity = originalQueueItem.activity;
      copyQueueItem.optionIndex = originalQueueItem.optionIndex;
      copyQueueItem.repeat = Math.max(
        Math.min(originalQueueItem.repeat, 1),
        Math.floor(originalQueueItem.repeat / 2)
      );
      originalQueueItem.repeat = Math.ceil(originalQueueItem.repeat / 2);
      this.activityQueue.splice(queueItemIndex + 1, 0, copyQueueItem);
      this.requestUpdate();
    });

    this.addEventListener('queue-item-deleted', (event) => {
      this.activityQueue = this.activityQueue.filter(
        (queueItem) => queueItem != event.composedPath()[0]
      );
      this.requestUpdate();
    });

    this.addEventListener('queue-item-reorder', (event) => {
      const e = event as CustomEvent;
      const oldIndex = e.detail.from;
      const newIndex = e.detail.to;

      const element = this.activityQueue.splice(oldIndex, 1)[0];
      this.activityQueue.splice(newIndex, 0, element);

      // check if the old item was active
      if (oldIndex == this.activityQueueIndex) {
        this.activityQueueIndex = oldIndex;
      }
      // check if the new location is active
      if (newIndex == this.activityQueueIndex) {
        this.activityQueueIndex = newIndex;
      }

      // splice is in-place, so manually request an update
      this.requestUpdate();
    });

    this.addEventListener('queue-item-earlier', (event) => {
      // original index for the clicked item
      const oldIndex = this.activityQueue.findIndex(
        (queueItem) => queueItem == event.composedPath()[0]
      );
      if (oldIndex == 0) return;
      // check if the item is active
      if (oldIndex == this.activityQueueIndex) {
        this.activityQueueIndex -= 1;
      }
      // check if the new location is active
      if (oldIndex == this.activityQueueIndex - 1) {
        this.activityQueueIndex += 1;
      }
      // Modulo, not remainder, so -1 flips to the end
      this.activityQueueIndex =
        ((this.activityQueueIndex % this.activityQueue.length) +
          this.activityQueue.length) %
        this.activityQueue.length;
      this.activityQueue.splice(oldIndex, 1);
      this.activityQueue.splice(
        oldIndex - 1,
        0,
        event.composedPath()[0] as ActivityQueueItem
      );
      this.requestUpdate();
    });

    this.addEventListener('queue-item-later', (event) => {
      const oldIndex = this.activityQueue.findIndex(
        (queueItem) => queueItem == event.composedPath()[0]
      );
      // check if the item is active
      if (oldIndex == this.activityQueueIndex) {
        this.activityQueueIndex += 1;
      }
      // check if the new location is active
      if (oldIndex == this.activityQueueIndex + 1) {
        this.activityQueueIndex -= 1;
      }
      // Modulo, not remainder, so -1 flips to the end
      this.activityQueueIndex =
        ((this.activityQueueIndex % this.activityQueue.length) +
          this.activityQueue.length) %
        this.activityQueue.length;
      this.activityQueue.splice(oldIndex, 1);
      this.activityQueue.splice(
        oldIndex + 1,
        0,
        event.composedPath()[0] as ActivityQueueItem
      );
      this.requestUpdate();
    });
  }
  togglePause() {
    if (this.gameSpeed == 0) this.gameSpeed = 1;
    else if (this.gameSpeed == 1) this.gameSpeed = 0;
    if (this.gameSpeed == 0) {
      ri.classList.add('paused');
    } else {
      ri.classList.remove('paused');
    }
  }

  load(saveGame) {
    if (saveGame.version < 6) {
      return;
    }

    lastTime = saveGame.lastTime;
    lastSpellCooldownTime = saveGame.lastSpellCooldownTime;
    this.life = saveGame.life;
    this.tick = new Decimal(saveGame.tick);
    this.distance = new Decimal(saveGame.distance);
    this.nextLandmark = new Decimal(saveGame.nextLandmark);
    this.unlockedPanels = new Set(saveGame.unlockedPanels);
    for (const resourceData of saveGame.resources) {
      const resource = this.resources[resourceData.name];
      if (resource) {
        resource.baseCapacity = new Decimal(resourceData.baseCapacity);
        // Set _value to bypass the setter call (beforeGain etc.)
        resource._value = new Decimal(resourceData.value);
        resource.unlocked = resourceData.unlocked;
        resource.secret = resourceData.secret;
        resource.new = resourceData.new;
        resource.hiddenByPlayer = resourceData.hiddenByPlayer;
      }
    }
    for (const statData of saveGame.stats) {
      const stat = this.stats[statData.name];
      if (stat) {
        stat.value = new Decimal(statData.value);
        stat.unlocked = statData.unlocked;
        stat.secret = statData.secret;
        stat.new = statData.new;
        stat.hiddenByPlayer = statData.hiddenByPlayer;
      }
    }
    for (const activityData of saveGame.activities) {
      const activity = this.activities[activityData.name];
      if (activity) {
        activity.uses = new Decimal(activityData.uses);
        activity.unlocked = activityData.unlocked;
        activity.secret = activityData.secret;
        activity.new = activityData.new;
        activity.hiddenByPlayer = activityData.hiddenByPlayer;
      }
    }
    for (const spellData of saveGame.spells) {
      const spell = this.spells[spellData.name];
      if (spell) {
        spell.uses = new Decimal(spellData.uses);
        spell.unlocked = spellData.unlocked;
        spell.secret = spellData.secret;
        spell.new = spellData.new;
        spell.hiddenByPlayer = spellData.hiddenByPlayer;
        spell.currentCooldown = new Decimal(spellData.currentCooldown);
      }
    }
    this.activityQueue = [];
    for (const queueItemData of saveGame.loadouts[
      saveGame.selectedLoadoutIndex ?? 0
    ]) {
      const queueItem = document.createElement(
        'ri-activity-queue-item'
      ) as ActivityQueueItem;
      if (queueItemData.name in this.activities)
        queueItem.activity = this.activities[queueItemData.name];
      else if (queueItemData.name in this.spells)
        queueItem.activity = this.spells[queueItemData.name];
      else throw new Error('Unknown activity or spell: ' + queueItemData.name);
      queueItem.repeat = queueItemData.repeat;
      queueItem.activeIndex = 0;
      queueItem.option = queueItemData.option ?? queueItem.activity.options[0];
      this.activityQueue.push(queueItem);
    }
    for (const i in this.loadouts) {
      // Reset the loadout to default
      this.loadouts[i] = [];
      // Add the queues to the loadout
      if (saveGame.loadouts?.[i]) {
        for (const queueItemData of saveGame.loadouts[i]) {
          const queueItem = document.createElement(
            'ri-activity-queue-item'
          ) as ActivityQueueItem;
          if (queueItemData.name in this.activities)
            queueItem.activity = this.activities[queueItemData.name];
          else if (queueItemData.name in this.spells)
            queueItem.activity = this.spells[queueItemData.name];
          else
            throw new Error('Unknown activity or spell: ' + queueItemData.name);
          queueItem.repeat = queueItemData.repeat;
          queueItem.activeIndex = 0;
          queueItem.option =
            queueItemData.option ?? queueItem.activity.options[0];
          this.loadouts[i].push(queueItem);
        }
      }
    }
    this.lastLoadoutIndex = saveGame.selectedLoadoutIndex;
    function logEntryFromSave(entryData) {
      if (entryData.cost || entryData.effect) {
        const activityLogEntry = document.createElement(
          'ri-activity-log-entry'
        ) as ActivityLogEntry;
        activityLogEntry.name = entryData.name;
        activityLogEntry.displayName = entryData.displayName;
        activityLogEntry.cost = new Map(entryData.cost);
        activityLogEntry.effect = new Map(entryData.effect);
        activityLogEntry.logString = entryData.logString;
        activityLogEntry.tick = entryData.tick;
        activityLogEntry.tags = new Set(entryData.tags);
        return activityLogEntry;
      }
      const logEntry = document.createElement('ri-log-entry') as LogEntry;
      logEntry.msg = entryData.msg;
      logEntry.tick = entryData.tick;
      logEntry.tags = new Set(entryData.tags);
      return logEntry;
    }
    if (saveGame.log) {
      this.logEntries = saveGame.log.map((entry) => logEntryFromSave(entry));
    }
    if (saveGame.history) {
      this.history = saveGame.history?.map((el) => {
        return Object.fromEntries(
          Object.entries(el).map(([k, v]) => {
            try {
              v = [new Decimal(v[0]), new Decimal(v[1])];
            } catch (e) {
              return [k, 0];
            }
            return [k, v];
          })
        );
      });
    }
    if (saveGame.preferences) {
      this.preferences = saveGame.preferences;
    }
  }
  reincarnate() {
    this.log(
      'Suddenly, you find yourself back at the portal. You remember some things from your past lives that might make your life easier.'
    );
    for (const unlockable of this.unlockables) {
      if (!unlockable.persistentUnlock) {
        unlockable.unlocked = false;
      }
      if (!unlockable.persistentValue) {
        unlockable.resetToInitials();
      }
    }
    this.life += 1;
    this.tick = new Decimal(0);
    this.distance = new Decimal(0);
    this.nextLandmark = new Decimal(1000);
    this.activityQueue = this.activityQueue.filter((queueItem) =>
      queueItem.activity?.condition()
    );
  }

  static override styles = [
    global_css,
    css`
      :host {
        display: grid;
        grid-template-columns: minmax(400px, 1fr) 2fr minmax(400px, 1fr);
        grid-template-rows: 48px minmax(12em, 2fr) 2fr 2fr minmax(12em, 3fr);
        grid-column-gap: 12px;
        grid-row-gap: 8px;
        grid-template-areas:
          'speed-controls distance dev'
          'resources activity-queue right'
          'resources activity-queue right'
          'resources activity-queue right'
          'resources log right';
        height: 100%;
        overflow: auto;
        background-color: rgb(var(--background-color));
      }

      :host > * {
        border: 2px #0004 solid;
        border-radius: 4px;
        padding: 8px;
        background: #fff4;
      }

      .dev {
        grid-area: dev;
        display: flex;
        flex-direction: row;
        align-items: center;
      }
      .dev > * {
        margin: 4px;
      }
      #speed-controls {
        grid-area: speed-controls;
        display: flex;
        align-items: center;
      }
      #speed-controls > * {
        margin: 2px;
      }

      #resources {
        grid-area: resources;
        overflow-y: auto;
      }
      #resources > * {
        --bg-color: rgba(var(--value-color), 0.2);
      }

      #activities {
        grid-area: activities;
      }

      #activities-container {
        display: flex;
        direction: row;
        flex-wrap: wrap;
        overflow-y: auto;
      }

      #stats {
        grid-area: stats;
        overflow-y: auto;
      }

      #distance {
        grid-area: distance;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
      }

      #distance > * {
        white-space: nowrap;
      }

      #distance progress {
        width: 100%;
        height: 32px;
      }

      #distance progress::-webkit-progress-value {
        background-color: rbg(var(--value-color, green));
      }

      #world {
        grid-area: world;
        overflow-y: auto;
      }

      #world progress {
        width: 64px;
      }

      #activity-queue {
        grid-area: activity-queue;
        overflow-y: auto;
      }

      #activity-queue-header {
        display: flex;
        justify-content: space-between;
      }

      #activity-queue-container {
        display: flex;
        width: 100%;
        flex-direction: column;
        align-items: start;
        gap: 2px;
      }

      #activity-queue.drag-hover {
        background: #0002;
      }

      #activity-queue-container activity-queue-item {
        margin: 4px;
      }

      ri-log {
        grid-area: log;
      }

      ri-log#activity-log {
        grid-area: activity-log;
      }

      #spells {
        grid-area: spells;
      }

      #unlockables {
        grid-area: unlockables;
      }

      #speed-select {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
      }

      .speed-select-item {
        border: 1px solid black;
        border-radius: 4px;
        margin: 2px;
        padding: 2px 4px;
      }

      .speed-select-item.selected {
        background: #0004;
      }

      #stats-container {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
      }
      #stats-container > * {
        margin: 2px;
      }
    `,
  ];

  lastLightValue = new Decimal(NaN);
  lastDarkMode = false;
  lastLightEffects = true;
  override render() {
    // This is delicate, relies on no one else changing global_css
    // Also extremenly expensive cause of layout.
    // Two weeks later and I have no idea how this works, comment your code
    let backgroundColor = new Color('#66ccff');
    let refresh = false;

    if (this.preferences.darkMode) {
      backgroundColor = new Color('#00111a');
    }
    if (this.lastDarkMode != this.preferences.darkMode) {
      refresh = true;
      this.lastDarkMode = this.preferences.darkMode;
    }
    if (this.lastLightEffects != this.preferences.lightEffects) {
      refresh = true;
      this.lastLightEffects = this.preferences.lightEffects;
    }

    if (refresh || !this.resources.light.value.eq(this.lastLightValue)) {
      if (insertedCSS) global_css.styleSheet?.deleteRule(0);
      if (ri.preferences.lightEffects) {
        backgroundColor.hwb.w *= this.resources.light.value
          .div(100)
          .plus(1)
          .toNumber();
        backgroundColor.hwb.b *= this.resources.light.value
          .div(100)
          .times(-1)
          .plus(1)
          .toNumber();
        backgroundColor.lch.c *= this.resources.light.value
          .div(100)
          .div(3)
          .plus(1)
          .toNumber();
        backgroundColor.lch.l *= this.resources.light.value
          .div(100)
          .div(3)
          .plus(1)
          .toNumber();
      }
      const maxColorComponent = Math.max(
        backgroundColor.r,
        backgroundColor.g,
        backgroundColor.b,
        1
      );
      global_css.styleSheet?.insertRule(
        `*{--background-color: ${(
          (backgroundColor.r * 255) /
          maxColorComponent
        ).toFixed(0)} ${((backgroundColor.g * 255) / maxColorComponent).toFixed(
          0
        )} ${((backgroundColor.b * 255) / maxColorComponent).toFixed(0)};}`,
        0
      );
      insertedCSS = true;
    }
    this.lastLightValue = this.resources.light.value;
    return html`
      <div id="speed-controls">
        <div
          id="speed-select"
          @click=${(e) => {
            this.gameSpeed = e.composedPath()[0].getAttribute('data-value');
            debugger;
            if (this.gameSpeed == 0) {
              ri.classList.add('paused');
            } else {
              ri.classList.remove('paused');
            }
          }}
        >
          <div
            class="button speed-select-item ${
              this.gameSpeed == 0 ? 'selected' : nothing
            }"
            data-value=${0}
            data-tippy-content="Accumulate Time."
          >
            ⏸
          </div>
          <div
            class="button speed-select-item ${
              this.gameSpeed == 1 ? 'selected' : nothing
            }"
            data-value=${1}
            data-tippy-content="Each second, one second passes."
          >
            ▶
          </div>
          <!-- <div class="button speed-select-item" data-value=${2}>2x</div>
          <div class="button speed-select-item" data-value=${5}>5x</div>
          <div class="button speed-select-item" data-value=${10}>10x</div> -->
        </div>
        时间： ${formatTime(this.tick.times(tickLength / 1000))}
        
        <input
          type="checkbox"
          class="filter"
          id="use-saved-time"
          @change=${(e) => {
            this.useSavedTime = e.target.checked;
          }}
        /><label class="chip" id="use-saved-time-toggle" hidden for="use-saved-time">使用节省的时间： ${formatTime(
          this.resources.time.value.floor()
        )}</label>
      </div>
      <ri-resources
        id="resources"
        .filterables=${Object.values(this.resources)}
      ></ri-resources>
      </div>
      <div id="stats" class="locked">
        <h4>Stats</h4>
        <div id="stats-container" class="container">
          ${Object.values(this.stats).filter((s) => s.unlocked)}
        </div>
      </div>
      <ri-activities
        id="activities"
        class="locked"
        .activities=${Object.values(this.activities)}
      ></ri-activities>
      <div id="activity-queue">
        <div id="activity-queue-header">
          <h4>
            活动队列ueue<span>
              - 你的行动计划！ - 如果这开始变得奇怪，请刷新页面！
              这样可以节约时间。<br>Ctrl + click = 5x, Shift + click = 10x, Ctrl + Shift + click = 50x</span
            >
          </h4>
          <select
                @change=${(event: Event) => {
                  this.loadouts[this.lastLoadoutIndex] = this.activityQueue;
                  this.lastLoadoutIndex = Number(
                    (event.target as HTMLOptionElement)?.value
                  );
                  this.activityQueue = this.loadouts[this.lastLoadoutIndex];
                  this.activityQueueIndex = 0;
                  this.requestUpdate();
                }}
          >${this.loadouts.map(
            (loadout, i) =>
              html`<option value=${i} ?selected=${this.lastLoadoutIndex === i}>
                Loadout ${i}
              </option>`
          )}</select>
          <span
            >${
              this.activityQueue.reduce(
                (acc, queueItem, i) =>
                  i < this.activityQueueIndex
                    ? acc + queueItem.totalLength
                    : acc,
                0
              ) + this.activityQueue[this.activityQueueIndex]?.activeIndex
            }
            / ${this.activityQueue.reduce((acc, q) => acc + q.totalLength, 0)}
          </span>
        </div>
        <div id="activity-queue-container">${Object.values(
          this.activityQueue
        )}</div>
      </div>
      <ri-log id="log" .filterables=${[...this.logEntries].reverse()}></ri-log>
      <ri-unlockables
        id="unlockables"
        class="locked"
        .filterables=${this.unlockables}
        .entryClass=${'ri-unlockables-entry'}
      ></ri-unlockables>
      <div id="distance" class="distance">
        <h1>
          Distance:
          <span class="fancy distance"
            ><smooth-value .value=${this.distance}></smooth-value
          ></span>
          m
        </h1>
        <progress
          id="distance-progress"
          value=${this.distance.valueOf()}
          max=${this.nextLandmark.valueOf()}
        ></progress>
        <h1>${formatEng(this.nextLandmark, 1)} m</h1>
      </div>
      <ri-spells
        id="spells"
        class="locked"
        .spells=${Object.values(this.spells)}
      ></ri-spells>
      <div class="dev">
        <h4>Dev Tools</h4>
        <input
          type="text"
          @input=${(event: Event) => {
            if (event.target.value == 'bandit') {
              event.target.nextElementSibling.hidden = false;
              event.target.hidden = true;
            }
          }}
        />
        <div class="container" hidden>
          <button
            @click=${() => {
              this.resetSave();
            }}
          >
            Wipe and Reload
          </button>
          <button
            @click=${() => {
              this.unlockables.forEach((unlockable: UnlockableLitElement) =>
                unlockable.tryUnlock(true)
              );
              this.distance = new Decimal('1e6');
            }}
          >
            Unlock all unlockables
          </button>
          <button @click=${this.reincarnate}>Reincarnate</button>
        </div>
        <button
          @click=${() => {
            window.open(
              'https://www.youtube.com/embed/dQw4w9WgXcQ?rel=0&autoplay=1',
              undefined,
              'popup'
            );
          }}
        >
          Don't click
        </button>
        <button
          @click=${() => {
            settings();
          }}
        >
          Settings
        </button>
      </div>
    `;
  }
}
